import { useState, useCallback, useRef, useEffect, useMemo, type FormEvent } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, horizontalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createPortal } from "react-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Camera, ChevronRight, ChevronLeft, X, Check, Utensils,
  Search, Tag, Zap, MessageSquare, Gift, Megaphone, ChefHat,
  PartyPopper, Image as ImageIcon, Video, Share2, Loader2,
  Plus, Clock, Heart, Eye, CircleDot, ZoomIn, ZoomOut, Maximize2,
  Pencil, RefreshCw, Settings, Link2, Truck, DollarSign, Percent,
  MessageCircle, GripVertical, Star,
} from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useUpload } from "@/hooks/use-upload";
import { cn } from "@/lib/utils";
import PartnerLayout from "./PartnerLayout";
import CameraFirstCapture, { type CapturedMedia } from "@/components/CameraFirstCapture";
import { SocialMediaPreview, EatzyPostMockup, InstagramMockup, FacebookMockup, InstagramStoryMockup } from "@/components/partner/SocialMediaPreview";

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  available: boolean;
  menuId: number;
  categoryId: number;
}

interface MenuCategory {
  id: number;
  name: string;
  menuId: number;
  items: MenuItem[];
}

interface MenuData {
  categories: MenuCategory[];
  itemMenuMap: Record<string, number>;
}

interface SocialConnection {
  id: string;
  platform: 'facebook' | 'instagram' | 'tiktok';
  platformAccountName: string | null;
  platformAccountHandle: string | null;
  isActive: boolean;
}

interface PartnerPostItem {
  id: string;
  text: string;
  postType: string;
  promotionDetails: {
    type: string;
    title: string;
    couponCode?: string;
    expiresAt?: string;
  } | null;
  likes: number;
  comments: number;
  views?: number;
  createdAt: string;
  images?: string[];
}

type Step = 'camera' | 'edit' | 'menu-item' | 'compose' | 'distribute' | 'carousel-picker' | 'carousel-reorder';

const POST_TYPES = [
  { value: 'daily_special', label: 'Daily Special', icon: Star },
  { value: 'feature', label: 'Feature', icon: Tag },
  { value: 'limited_time_only', label: 'Limited Time Only', icon: Clock },
  { value: 'from_the_chef', label: 'From the Chef', icon: ChefHat },
  { value: 'menu_update', label: 'Menu Highlight', icon: Utensils },
  { value: 'promotion', label: 'Deal / Promotion', icon: Gift },
  { value: 'event', label: 'Event', icon: PartyPopper },
  { value: 'announcement', label: 'Announcement', icon: Megaphone },
];

// Quick-launch shortcuts shown as large tiles at the top of the type selector.
// These types cover the vast majority of partner posts and skip straight to media.
const QUICK_LAUNCH_TYPES = ['daily_special', 'promotion', 'from_the_chef'];

// Display helper: maps legacy 'deals' value (used in older posts) to the merged 'promotion' entry.
function resolvePostType(value: string) {
  const normalized = value === 'deals' ? 'promotion' : value;
  return POST_TYPES.find(p => p.value === normalized) ?? { value: normalized, label: normalized, icon: Megaphone };
}

const CONNECTION_POST_TYPES = new Set(['from_the_chef', 'event', 'announcement']);

const CAPTION_STARTERS: Record<string, string[]> = {
  from_the_chef: [
    'Quick update from our kitchen —',
    "I want to share something we've been working on —",
    'From my hands to your table —',
  ],
  event: [
    "Something special is happening here —",
    'Mark your calendar —',
    "We're hosting something you don't want to miss —",
  ],
  announcement: [
    'Big news from our team —',
    "We can't keep this to ourselves any longer —",
    'Something exciting is coming —',
  ],
};

const PLATFORM_META: Record<string, { label: string; icon: React.FC<{ className?: string }> }> = {
  facebook: { label: 'Facebook', icon: ({ className }) => <SiFacebook className={className} /> },
  instagram: { label: 'Instagram', icon: ({ className }) => <SiInstagram className={className} /> },
  instagram_story: { label: 'Instagram Story', icon: ({ className }) => <SiInstagram className={className} /> },
};

function generateCouponCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function computeExpiresAt(duration: 'today' | 'one_week' | '30_days'): string {
  const d = new Date();
  if (duration === 'today') {
    d.setHours(23, 59, 59, 0);
  } else if (duration === 'one_week') {
    d.setDate(d.getDate() + 7);
    d.setHours(23, 59, 59, 0);
  } else {
    d.setDate(d.getDate() + 30);
    d.setHours(23, 59, 59, 0);
  }
  return d.toISOString();
}

function stepLabel(step: Step) {
  switch (step) {
    case 'camera': return 'Snap a Photo';
    case 'edit': return 'Crop & Edit';
    case 'menu-item': return 'Tag a Dish';
    case 'compose': return 'Write Caption';
    case 'distribute': return 'Post Everywhere';
    case 'carousel-picker': return 'Pick Items';
    case 'carousel-reorder': return 'Arrange & Caption';
  }
}

const STEPS: Step[] = ['edit', 'menu-item', 'compose', 'distribute'];

export default function PartnerPost() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const restaurantProfile = localStorage.getItem('partnerRestaurantProfile');
  const restaurantName = localStorage.getItem('partnerRestaurantName') || '';
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  const authToken = localStorage.getItem('auth_token');

  // When arriving via the bottom-nav "+" button (?quickMode=1), open the type selector immediately.
  // Direct page visits or refreshes (no param) show the post list instead.
  const urlQuickMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('quickMode') === '1';
  const [showTypeSelector, setShowTypeSelector] = useState(urlQuickMode);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [quickMode, setQuickMode] = useState(true);
  const [step, setStep] = useState<Step>('menu-item');
  const [showCelebration, setShowCelebration] = useState(false);
  const [videoSkippedMenuStep, setVideoSkippedMenuStep] = useState(false);

  const [capturedMedia, setCapturedMedia] = useState<CapturedMedia | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [menuSearch, setMenuSearch] = useState('');

  const [savePhotoToMenu, setSavePhotoToMenu] = useState(true);

  // Carousel mode state
  const [isCarouselMode, setIsCarouselMode] = useState(false);
  const [carouselItems, setCarouselItems] = useState<{ id: string; name: string; image: string; price: number; caption: string }[]>([]);
  const [carouselOverallCaption, setCarouselOverallCaption] = useState('');

  const kudozRestaurantUuid = localStorage.getItem('partnerKudozRestaurantUuid');
  const orderingLink = useMemo(() => {
    if (!restaurantId || !kudozRestaurantUuid) return '';
    const linkUrl = new URL(`/restaurant/${kudozRestaurantUuid}`, window.location.origin);
    if (selectedItem?.id) {
      linkUrl.searchParams.set('item', String(selectedItem.id));
    }
    linkUrl.searchParams.set('source', 'social');
    return linkUrl.toString();
  }, [restaurantId, kudozRestaurantUuid, selectedItem?.id]);

  const [postType, setPostType] = useState('daily_special');
  const [caption, setCaption] = useState('');
  const [captionManuallyEdited, setCaptionManuallyEdited] = useState(false);
  const [offerEnabled, setOfferEnabled] = useState(false);
  const [offerType, setOfferType] = useState<'coupon' | 'kudoz'>('coupon');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscountType, setCouponDiscountType] = useState<'$5_off' | '10%_off' | 'free_delivery' | null>(null);
  const [offerDuration, setOfferDuration] = useState<'today' | 'one_week' | '30_days' | null>(null);
  const [kudozBonusType, setKudozBonusType] = useState<string | null>(null);
  const [selectedExistingCouponId, setSelectedExistingCouponId] = useState<number | null>(null);
  const [selectedExistingCouponCode, setSelectedExistingCouponCode] = useState<string>('');

  const [crossPostPlatforms, setCrossPostPlatforms] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('partnerLastPlatforms');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const platformsDefaultApplied = useRef(false);
  const [platformOverrides, setPlatformOverrides] = useState<Record<string, string>>({});
  const [igHashtagsInFirstComment, setIgHashtagsInFirstComment] = useState(true);

  const [editingPost, setEditingPost] = useState<PartnerPostItem | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editPostType, setEditPostType] = useState('');
  const [editTitle, setEditTitle] = useState('');

  const { uploadFile } = useUpload({
    onError: (e) => {
      const msg = e.message || 'Upload failed. Please try again.';
      const isSizeError = msg.toLowerCase().includes('too large') || msg.toLowerCase().includes('under 100 mb');
      toast({
        title: isSizeError ? 'File too large' : 'Upload failed',
        description: isSizeError ? `${msg} Try trimming or compressing the file.` : msg,
        variant: 'destructive',
      });
    },
  });

  const { data: menuData, isLoading: menuLoading } = useQuery<MenuData>({
    queryKey: ['/api/partner/menu', restaurantProfile],
    queryFn: async () => {
      if (!restaurantProfile) throw new Error('No restaurant selected');
      const res = await fetch(`/api/partner/menu/${restaurantProfile}`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken || '',
          'X-Restaurant-Id': restaurantId || '',
        },
      });
      if (!res.ok) throw new Error('Failed to fetch menu');
      return res.json();
    },
    enabled: !!restaurantProfile && showComposer,
    staleTime: 5 * 60 * 1000,
  });

  const { data: socialConnections } = useQuery<SocialConnection[]>({
    queryKey: ['/api/partner/social/connections', restaurantId],
    queryFn: async () => {
      const authToken = localStorage.getItem('auth_token');
      const eatzyToken = sessionStorage.getItem('partnerEatzyToken') || localStorage.getItem('eatzyUserToken') || '';
      const res = await fetch(`/api/partner/social/connections?restaurantId=${restaurantId}`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken,
        },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!restaurantId && showComposer,
    staleTime: 5 * 60 * 1000,
  });

  const { data: posts, isLoading: postsLoading } = useQuery<PartnerPostItem[]>({
    queryKey: ['/api/partner/posts', restaurantId],
    enabled: !!restaurantId,
  });

  useEffect(() => {
    if (platformsDefaultApplied.current) return;
    if (!socialConnections?.length) return;
    const saved = localStorage.getItem('partnerLastPlatforms');
    if (saved !== null) return;
    const defaultPlatforms: string[] = socialConnections.filter(c => c.isActive).map(c => c.platform);
    if (!defaultPlatforms.length) return;
    if (defaultPlatforms.includes('instagram') && !defaultPlatforms.includes('instagram_story')) {
      defaultPlatforms.push('instagram_story');
    }
    platformsDefaultApplied.current = true;
    setCrossPostPlatforms(defaultPlatforms);
  }, [socialConnections]);

  const businessId = useMemo<number | null>(() => {
    try {
      const id = JSON.parse(restaurantProfile || '{}').business_id;
      return id ? Number(id) : null;
    }
    catch { return null; }
  }, [restaurantProfile]);

  const restaurantIdNum = restaurantId ? parseInt(restaurantId) : null;

  const { data: existingCoupons = [] } = useQuery<any[]>({
    queryKey: ['/api/partner/coupons', businessId, restaurantIdNum, eatzyToken],
    queryFn: async () => {
      if (!eatzyToken || !restaurantIdNum || !businessId) return [];
      const res = await fetch(`/api/partner/coupons/${businessId}?restaurantId=${restaurantIdNum}`, {
        headers: {
          'X-Eatzy-Token': eatzyToken,
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data.filter((c: any) => c.active) : [];
    },
    enabled: !!eatzyToken && !!restaurantIdNum && offerEnabled && offerType === 'coupon',
    staleTime: 60 * 1000,
  });

  const { data: walletData } = useQuery<{ balance: number }>({
    queryKey: ['/api/partner/wallet'],
    enabled: offerEnabled,
    staleTime: 30 * 1000,
  });
  const walletBalance = walletData?.balance ?? null;

  const { data: kudozBonusesData } = useQuery<{ bonuses: number[] }>({
    queryKey: ['/api/partner/posts/kudoz-bonuses'],
    enabled: !!restaurantId && offerEnabled && offerType === 'kudoz',
    staleTime: 5 * 60 * 1000,
  });
  const previousKudozBonuses = kudozBonusesData?.bonuses ?? [];

  const generateSuggestedCaption = useCallback((type: string, item: MenuItem | null, name: string) => {
    const itemName = item?.name || '';
    const desc = (item?.description || '').trimEnd().replace(/[.!?]+$/, '');
    const price = item ? `$${item.price.toFixed(2)}` : '';
    // Normalize legacy 'deals' value to 'promotion' so old posts get the right captions.
    const resolvedType = type === 'deals' ? 'promotion' : type;

    const templates: Record<string, string[]> = {
      daily_special: [
        itemName
          ? `Today's daily special: ${itemName}${desc ? ` — ${desc}` : ''}. Come taste it while it lasts!`
          : `Today's special is here and it's not to be missed. Stop by and see what we've got for you!`,
        itemName
          ? `Fresh today — ${itemName}${price ? ` (${price})` : ''}${desc ? `. ${desc}` : ''}. Only available today!`
          : `Daily special alert! We've got something delicious waiting for you today. Come grab it!`,
      ],
      feature: [
        itemName
          ? `Spotlight on ${itemName}${desc ? ` — ${desc}` : ''}. One of our absolute favorites and a must-try at ${name || 'our restaurant'}.`
          : `We're putting the spotlight on one of our standout dishes. Come in and experience what makes ${name || 'us'} special.`,
        itemName
          ? `Featured dish: ${itemName}${price ? ` (${price})` : ''}${desc ? `. ${desc}` : ''}. This one speaks for itself!`
          : `Featured this week at ${name || 'our restaurant'} — a dish worth every single bite.`,
      ],
      limited_time_only: [
        itemName
          ? `Limited time only: ${itemName}${desc ? ` — ${desc}` : ''}${price ? ` for just ${price}` : ''}. Don't wait — it won't be here forever!`
          : `This won't be around for long! Come in and grab our limited-time offering before it's gone.`,
        itemName
          ? `Get it before it's gone! ${itemName}${desc ? ` — ${desc}` : ''}. Available for a limited time only at ${name || 'our restaurant'}.`
          : `Limited time, unlimited flavor. Our exclusive item is here — for now. Come see what everyone's talking about!`,
      ],
      from_the_chef: [
        itemName
          ? `Quick update from our kitchen — we've been working on ${itemName}${desc ? ` and ${desc.toLowerCase()}` : ''}. I can't wait for you to try it.`
          : `Quick update from our kitchen — I want to share something we've been putting a lot of love into. Come see what we've got for you.`,
        itemName
          ? `From my hands to your table — ${itemName}${desc ? `. ${desc}` : ''}${price ? ` (${price})` : ''}. This one means a lot to me.`
          : `I want to share something we've been working on — this is the kind of cooking that reminds me why I love what I do.`,
      ],
      deals: [
        itemName
          ? `Deal alert! ${itemName}${price ? ` — just ${price}` : ''}${desc ? `. ${desc}` : ''}. Don't miss out on this one!`
          : `We're running a special deal you won't want to pass up. Come in and save big on something delicious.`,
        itemName
          ? `Save big on ${itemName}${desc ? ` — ${desc}` : ''}${price ? ` for only ${price}` : ''}. This deal won't last!`
          : `Hot deal at ${name || 'our restaurant'}! Treat yourself to something amazing at an incredible price.`,
      ],
      menu_update: [
        itemName
          ? `Have you tried our ${itemName}?${desc ? ` ${desc}.` : ''} One of the favorites on our menu${price ? ` at just ${price}` : ''}.`
          : `We just updated our menu with some exciting new options. Come check it out!`,
        itemName
          ? `${itemName} is calling your name.${desc ? ` ${desc}.` : ''} Stop by ${name || 'our restaurant'} and see for yourself!`
          : `Fresh menu additions are here. New flavors, new favorites — come try them today.`,
      ],
      promotion: [
        itemName
          ? `Deal alert! ${itemName}${price ? ` — just ${price}` : ''}${desc ? `. ${desc}` : ''}. Don't miss out on this one!`
          : `We've got a special deal running right now. Limited time only — come grab it before it's gone!`,
        itemName
          ? `Your next ${itemName} just got even better.${desc ? ` ${desc}.` : ''} Check out our latest offer!`
          : `Special offer alert! Treat yourself to something delicious at a great price.`,
      ],
      event: [
        `Something special is happening at ${name || 'our place'} and we want you there. Mark your calendar — this is one you don't want to miss.`,
        `We're hosting something you'll love — stay tuned for all the details. We can't wait to see you here.`,
      ],
      announcement: [
        `Big news from our team — we can't keep this to ourselves any longer. We're so excited to share this with you.`,
        `Something exciting is coming to ${name || 'our place'} and we think you're going to love it. Stay tuned!`,
      ],
    };

    const variants = templates[resolvedType] || templates.announcement;
    const idx = Math.floor(Math.random() * variants.length);
    return variants[idx];
  }, []);


  const isFirstPost = posts !== undefined && posts.length === 0;

  const createPostMutation = useMutation({
    mutationFn: async () => {
      const generalMedia = uploadedUrl
        ? [{ url: uploadedUrl, type: capturedMedia?.type === 'video' ? 'video' : 'image' }]
        : [];
      const items = selectedItem
        ? [{ name: selectedItem.name, description: selectedItem.description || '', menuItemId: selectedItem.id }]
        : [];

      let finalCouponCode: string | undefined;
      const computedExpiresAt = offerEnabled && offerDuration ? computeExpiresAt(offerDuration) : undefined;
      const discountLabelForOffer = couponDiscountType === '$5_off' ? '$5 Off'
        : couponDiscountType === '10%_off' ? '10% Off'
        : couponDiscountType === 'free_delivery' ? 'Free Delivery'
        : undefined;

      if (offerEnabled && offerType === 'coupon') {
        if (selectedExistingCouponId) {
          finalCouponCode = selectedExistingCouponCode;
        } else if (couponDiscountType && offerDuration && businessId) {
          const effectsInput: Record<string, any> = {};
          if (couponDiscountType === '$5_off') effectsInput.flat = 5;
          else if (couponDiscountType === '10%_off') effectsInput.percentage = 10;
          else if (couponDiscountType === 'free_delivery') effectsInput.free_delivery = true;

          const rulesInput: Record<string, any> = {};
          if (restaurantIdNum) rulesInput.restaurants = [restaurantIdNum];
          if (offerDuration) {
            const now = Math.floor(Date.now() / 1000);
            const to = Math.floor(new Date(computeExpiresAt(offerDuration)).getTime() / 1000);
            rulesInput.validity = { from: now, to };
          }

          const discountLabel = couponDiscountType === '$5_off' ? '$5 Off'
            : couponDiscountType === '10%_off' ? '10% Off'
            : 'Free Delivery';

          try {
            const couponRes = await fetch('/api/partner/coupons', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Eatzy-Token': eatzyToken || '',
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
              },
              credentials: 'include',
              body: JSON.stringify({
                business: businessId,
                name: `QuickPost ${discountLabel}`,
                code: couponCode,
                active: true,
                effects: effectsInput,
                rules: rulesInput,
              }),
            });
            if (couponRes.ok) {
              const created = await couponRes.json();
              finalCouponCode = created.code || couponCode;
              queryClient.invalidateQueries({ queryKey: ['/api/partner/coupons', businessId] });
            } else {
              finalCouponCode = couponCode;
            }
          } catch {
            finalCouponCode = couponCode;
          }
        }
      }

      let finalKudozBonus: number | '2x' | undefined;
      if (offerEnabled && offerType === 'kudoz' && kudozBonusType) {
        finalKudozBonus = kudozBonusType === '2x' ? '2x' : parseInt(kudozBonusType);
      }

      const res = await apiRequest('POST', '/api/partner/posts', {
        restaurantId,
        text: caption,
        promotionType: postType,
        promotionTitle: POST_TYPES.find(p => p.value === postType)?.label || '',
        couponCode: finalCouponCode,
        expiresAt: computedExpiresAt,
        kudozBonus: finalKudozBonus,
        link: orderingLink || undefined,
        generalMedia,
        items,
        crossPostPlatforms: crossPostPlatforms.length > 0 ? crossPostPlatforms : undefined,
      });
      const postData = await res.json();

      if (crossPostPlatforms.length > 0 && postData.id) {
        const authToken = localStorage.getItem('auth_token');
        const eatzyToken = sessionStorage.getItem('partnerEatzyToken') || localStorage.getItem('eatzyUserToken') || '';
        const crossPostLink = postData.shortLink || orderingLink || undefined;
        try {
          const socialRes = await fetch('/api/partner/social/post', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': authToken ? `Bearer ${authToken}` : '',
              'X-Eatzy-Token': eatzyToken,
            },
            body: JSON.stringify({
              partnerPostId: postData.id,
              restaurantId,
              content: {
                text: caption,
                title: POST_TYPES.find(p => p.value === postType)?.label,
                type: postType,
                mediaType: isCarouselMode ? 'carousel' : capturedMedia?.type === 'video' ? 'video' : 'image',
                imageUrl: !isCarouselMode && capturedMedia?.type !== 'video' ? (uploadedUrl || undefined) : undefined,
                videoUrl: capturedMedia?.type === 'video' ? (uploadedUrl || undefined) : undefined,
                carouselItems: isCarouselMode && carouselItems
                  ? carouselItems.map(ci => ({ imageUrl: ci.image, caption: ci.caption }))
                  : undefined,
                link: crossPostLink,
                promotionDetails: finalCouponCode ? {
                  couponCode: finalCouponCode,
                  discountLabel: discountLabelForOffer,
                  expiresAt: computedExpiresAt || undefined,
                } : undefined,
              },
              platforms: crossPostPlatforms,
              platformOverrides: Object.keys(platformOverrides).length > 0 ? platformOverrides : undefined,
              firstCommentHashtags: igHashtagsInFirstComment,
            }),
          });
          if (!socialRes.ok) {
            const errText = await socialRes.text().catch(() => socialRes.statusText);
            console.warn('[QuickPost] Social cross-post HTTP error:', socialRes.status, errText);
            toast({ title: 'Cross-post issue', description: 'Your Eatzy post is live, but some social platforms may not have received it.', variant: 'destructive' });
          } else {
            const socialData = await socialRes.json().catch(() => null);
            console.log('[QuickPost] Social cross-post results:', socialData);
            if (socialData?.results) {
              const failed = (socialData.results as { platform: string; success: boolean; error?: string }[]).filter(r => !r.success);
              if (failed.length > 0) {
                const platformDisplayNames: Record<string, string> = {
                  facebook: 'Facebook',
                  instagram: 'Instagram',
                  instagram_story: 'Instagram Story',
                  tiktok: 'TikTok',
                  eatzy: 'Eatzy',
                };
                const names = failed.map(r => platformDisplayNames[r.platform] ?? (r.platform.charAt(0).toUpperCase() + r.platform.slice(1))).join(' & ');
                const firstErr = failed[0].error || 'Unknown error';
                console.warn('[QuickPost] Platforms failed:', failed);
                toast({
                  title: `${names} not posted`,
                  description: firstErr.length > 120 ? firstErr.slice(0, 117) + '…' : firstErr,
                  variant: 'destructive',
                });
              }
            }
          }
        } catch (socialErr) {
          console.warn('[QuickPost] Social cross-post failed:', socialErr);
          toast({ title: 'Cross-post issue', description: 'Your Eatzy post is live, but cross-posting encountered a network error.', variant: 'destructive' });
        }
      }

      return postData;
    },
    onSuccess: () => {
      if (crossPostPlatforms.length > 0) {
        localStorage.setItem('partnerLastPlatforms', JSON.stringify(crossPostPlatforms));
      }
      queryClient.invalidateQueries({ queryKey: ['/api/partner/posts', restaurantId] });

      // Background write-back: save post photo to menu item (opt-in, silent on failure)
      if (savePhotoToMenu && selectedItem && uploadedUrl && capturedMedia?.type !== 'video' && menuData) {
        const menuId = menuData.itemMenuMap?.[selectedItem.id];
        if (menuId && restaurantId) {
          const at = localStorage.getItem('auth_token');
          const et = sessionStorage.getItem('partnerEatzyToken') || localStorage.getItem('eatzyUserToken') || '';
          fetch(`/api/partner/restmanager/${restaurantId}/menus/${menuId}/items/${selectedItem.id}/link-image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': at ? `Bearer ${at}` : '',
              'X-Eatzy-Token': et,
            },
            body: JSON.stringify({ imageUrl: uploadedUrl }),
          }).then(r => {
            if (r.ok) {
              queryClient.invalidateQueries({ queryKey: ['/api/partner/menu', restaurantProfile] });
            }
          }).catch(() => {});
        }
      }

      if (isFirstPost) {
        setShowCelebration(true);
        setShowComposer(false);
      } else {
        toast({ title: 'Posted!', description: 'Your post is live in the feed.' });
        closeAndReset();
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to post', description: err.message, variant: 'destructive' });
    },
  });

  const updatePostMutation = useMutation({
    mutationFn: async () => {
      if (!editingPost) throw new Error('No post selected');
      return apiRequest('PATCH', `/api/partner/posts/${editingPost.id}`, {
        text: editCaption,
        promotionType: editPostType || undefined,
        promotionTitle: editTitle || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/posts', restaurantId] });
      toast({ title: 'Saved!', description: 'Your post has been updated.' });
      setEditingPost(null);
    },
    onError: (err: Error) => {
      toast({ title: 'Failed to save', description: err.message, variant: 'destructive' });
    },
  });

  const openEditPost = useCallback((post: PartnerPostItem) => {
    setEditingPost(post);
    setEditCaption(post.text || '');
    const rawType = post.promotionDetails?.type || post.postType || '';
    setEditPostType(rawType === 'deals' ? 'promotion' : rawType);
    setEditTitle(post.promotionDetails?.title || '');
  }, []);

  const closeAndReset = useCallback(() => {
    setShowComposer(false);
    setShowCelebration(false);
    setShowTypeSelector(false);
    setQuickMode(true);
    setStep('menu-item');
    setVideoSkippedMenuStep(false);
    setCapturedMedia(null);
    setUploadedUrl(null);
    setSelectedItem(null);
    setMenuSearch('');
    setIsCarouselMode(false);
    setCarouselItems([]);
    setCarouselOverallCaption('');
    setPostType('daily_special');
    setCaption('');
    setCaptionManuallyEdited(false);
    setOfferEnabled(false);
    setCouponCode('');
    setCouponDiscountType(null);
    setOfferDuration(null);
    setKudozBonusType(null);
    setSelectedExistingCouponId(null);
    setSelectedExistingCouponCode('');
    setCrossPostPlatforms(() => {
      try {
        const saved = localStorage.getItem('partnerLastPlatforms');
        return saved ? JSON.parse(saved) : [];
      } catch { return []; }
    });
    setPlatformOverrides({});
    setSavePhotoToMenu(true);
  }, []);

  // Listen for the custom event dispatched by the bottom-nav "+" button when already on this page.
  // closeAndReset() ensures any open composer/media surfaces are fully cleared before the selector reopens.
  useEffect(() => {
    const handler = () => { closeAndReset(); setShowTypeSelector(true); };
    window.addEventListener('quickpost:open', handler);
    return () => window.removeEventListener('quickpost:open', handler);
  }, [closeAndReset]);

  const uploadMedia = useCallback(async (media: CapturedMedia) => {
    const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
    if (media.type === 'video' && media.file.size > MAX_VIDEO_BYTES) {
      toast({
        title: 'Video too large',
        description: 'Please keep videos under 100 MB. Try trimming or compressing the clip.',
        variant: 'destructive',
      });
      return;
    }
    setCapturedMedia(media);
    setIsUploading(true);
    try {
      const result = await uploadFile(media.file);
      if (result) setUploadedUrl(result.objectPath);
      // On failure, uploadFile calls onError (defined above) and returns null — no duplicate toast needed.
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      const isSizeError = msg.toLowerCase().includes('too large') || msg.toLowerCase().includes('under 100 mb');
      toast({
        title: isSizeError ? 'File too large' : 'Upload failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  }, [uploadFile, toast]);

  const handleCapture = useCallback(async (media: CapturedMedia) => {
    await uploadMedia(media);
    if (media.type === 'video' && CONNECTION_POST_TYPES.has(postType)) {
      setVideoSkippedMenuStep(true);
      setStep('compose');
    } else {
      setStep('menu-item');
    }
  }, [uploadMedia, postType]);

  const handleMediaPickerCapture = useCallback((media: CapturedMedia) => {
    const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
    if (media.type === 'video' && media.file.size > MAX_VIDEO_BYTES) {
      toast({
        title: 'Video too large',
        description: 'Please keep videos under 100 MB. Try trimming or compressing the clip.',
        variant: 'destructive',
      });
      return;
    }
    setShowMediaPicker(false);
    setShowComposer(true);
    setCapturedMedia(media);
    if (media.type === 'video' && CONNECTION_POST_TYPES.has(postType)) {
      uploadMedia(media);
      setVideoSkippedMenuStep(true);
      setStep('compose');
    } else if (media.type === 'video') {
      uploadMedia(media);
      setStep('menu-item');
    } else {
      setStep('edit');
    }
  }, [uploadMedia, toast, postType]);

  const handleEditorDone = useCallback(async (croppedDataUrl: string) => {
    const res = await fetch(croppedDataUrl);
    const blob = await res.blob();
    const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    const editedMedia: CapturedMedia = { type: 'photo', file, url: croppedDataUrl };
    await uploadMedia(editedMedia);
    setStep('menu-item');
  }, [uploadMedia]);

  const handleMediaPickerSkip = useCallback(() => {
    setShowMediaPicker(false);
    setStep('menu-item');
    setShowComposer(true);
  }, []);

  const handleSkipMedia = useCallback(() => {
    setStep('menu-item');
  }, []);

  const canAdvanceFromCompose = useMemo(() => {
    if (!caption.trim()) return false;
    if (offerEnabled) {
      if (offerType === 'coupon') {
        if (!offerDuration) return false;
        if (selectedExistingCouponId) return true;
        return couponDiscountType !== null;
      }
      if (offerType === 'kudoz') {
        if (walletBalance === 0) return false;
        return kudozBonusType !== null && offerDuration !== null;
      }
    }
    return true;
  }, [caption, offerEnabled, offerType, selectedExistingCouponId, couponDiscountType, offerDuration, kudozBonusType, walletBalance]);

  const allMenuItems = menuData?.categories?.flatMap(c => c.items) || [];
  const filteredItems = menuSearch.trim()
    ? allMenuItems.filter(i =>
        i.name.toLowerCase().includes(menuSearch.toLowerCase()) ||
        (i.description || '').toLowerCase().includes(menuSearch.toLowerCase())
      )
    : allMenuItems;

  const activeSocialPlatforms = (socialConnections || []).filter(c => c.isActive);
  const hasConnectedPlatforms = activeSocialPlatforms.length > 0;

  const togglePlatform = (platform: string) => {
    setCrossPostPlatforms(prev => {
      if (prev.includes(platform)) {
        // Removing instagram also removes instagram_story
        if (platform === 'instagram') {
          return prev.filter(p => p !== 'instagram' && p !== 'instagram_story');
        }
        return prev.filter(p => p !== platform);
      } else {
        // Adding instagram auto-adds instagram_story when there's an ordering link
        if (platform === 'instagram' && orderingLink) {
          return [...prev, 'instagram', 'instagram_story'];
        }
        return [...prev, platform];
      }
    });
  };

  useEffect(() => {
    if (!orderingLink) {
      setCrossPostPlatforms(prev => prev.filter(p => p !== 'instagram_story'));
    }
  }, [orderingLink]);

  const currentStepIndex = STEPS.indexOf(step);

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <PartnerLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-post-title">QuickPost</h1>
            <p className="text-muted-foreground text-sm">Snap, tag, post — turn content into orders</p>
          </div>
          <Button
            onClick={() => setShowTypeSelector(true)}
            data-testid="button-create-post"
            size="lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Post
          </Button>
        </div>

        {!postsLoading && (!posts || posts.length === 0) && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="rounded-full bg-primary/10 p-4">
                <Camera className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg">Your Instagram posts, now with order buttons</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                  Take a photo of your food, pick the menu item, and post to Eatzy + your social channels in one tap. 
                  Customers order directly from the post.
                </p>
              </div>
              <Button
                onClick={() => setShowTypeSelector(true)}
                className="mt-1"
                size="lg"
              >
                <Camera className="h-5 w-5 mr-2" />
                Create your first post
              </Button>
            </CardContent>
          </Card>
        )}

        {postsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="space-y-3">
            {posts.map(post => {
              const TypeIcon = resolvePostType(post.postType || '').icon;
              return (
                <Card key={post.id} data-testid={`post-card-${post.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      {post.images?.[0] ? (
                        <div className="shrink-0 w-16 h-16 rounded-md overflow-hidden bg-muted">
                          <img src={post.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ) : (
                        <div className="shrink-0 w-16 h-16 rounded-md bg-muted flex items-center justify-center">
                          <TypeIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {post.promotionDetails?.title && (
                          <p className="font-semibold text-sm leading-tight">{post.promotionDetails.title}</p>
                        )}
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{post.text}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{post.likes}</span>
                          <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{post.comments}</span>
                          {post.views !== undefined && (
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{post.views}</span>
                          )}
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(post.createdAt)}</span>
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="shrink-0 -mt-1 -mr-1"
                        onClick={() => openEditPost(post)}
                        aria-label="Edit post"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>

      {showCelebration && (
        <Dialog open={showCelebration} onOpenChange={(open) => !open && closeAndReset()}>
          <DialogContent className="max-w-sm text-center">
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-4">
                <PartyPopper className="h-10 w-10 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-bold">Your first post is live!</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Customers near your restaurant can now discover and order from your post. 
                  Keep posting daily to maximize visibility and orders.
                </p>
              </div>

              {(uploadedUrl || capturedMedia?.url) && (
                <div className="w-full rounded-lg overflow-hidden border bg-muted">
                  {capturedMedia?.type === 'video' ? (
                    <video
                      src={uploadedUrl || capturedMedia?.url}
                      className="w-full aspect-square object-cover"
                      muted
                      autoPlay
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={uploadedUrl || capturedMedia?.url}
                      alt="Your post"
                      className="w-full aspect-square object-cover"
                    />
                  )}
                  {caption && (
                    <div className="p-3 text-left">
                      <p className="text-xs font-semibold">{restaurantName}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{caption}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2 w-full">
                <Button onClick={() => { closeAndReset(); navigate('/partner'); }}>
                  <Check className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
                <Button variant="outline" onClick={() => { closeAndReset(); setShowTypeSelector(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Another Post
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Sheet open={showTypeSelector} onOpenChange={setShowTypeSelector}>
        <SheetContent side="bottom" className="rounded-t-xl pb-8">
          <SheetHeader className="mb-4">
            <SheetTitle>What kind of post?</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            {/* Quick-launch: 3 large tiles for the most common post types */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Quick Start</p>
              <div className="grid grid-cols-3 gap-2">
                {POST_TYPES.filter(pt => QUICK_LAUNCH_TYPES.includes(pt.value)).map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg border border-border hover-elevate text-center"
                    onClick={() => {
                      setPostType(value);
                      if (value === 'promotion' || value === 'deals') {
                        setOfferEnabled(true);
                        // Apply defaults only when not already configured (preserve mid-flow values)
                        if (!couponCode) setCouponCode(generateCouponCode());
                        if (!couponDiscountType) setCouponDiscountType('10%_off');
                        if (!offerDuration) setOfferDuration('today');
                      }
                      setShowTypeSelector(false);
                      // If media is already captured (mid-flow type change), skip media picker
                      if (capturedMedia || uploadedUrl) {
                        setStep('compose');
                        setShowComposer(true);
                      } else {
                        setShowMediaPicker(true);
                      }
                    }}
                  >
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="text-xs font-medium leading-tight">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Carousel option */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Carousel</p>
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover-elevate text-left w-full"
                onClick={() => {
                  setIsCarouselMode(true);
                  setPostType('daily_special');
                  setShowTypeSelector(false);
                  setShowComposer(true);
                  setStep('carousel-picker');
                }}
              >
                <ImageIcon className="h-4 w-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium block">Today's Specials</span>
                  <span className="text-xs text-muted-foreground">Swipeable carousel of 2–8 items</span>
                </div>
                <Badge variant="secondary" className="ml-2 text-xs shrink-0">Instagram + Facebook</Badge>
              </button>
            </div>

            {/* Remaining food post types (not in quick-launch) */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">More</p>
              <div className="space-y-0.5">
                {POST_TYPES.filter(pt => !CONNECTION_POST_TYPES.has(pt.value) && !QUICK_LAUNCH_TYPES.includes(pt.value)).map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md hover-elevate text-left w-full"
                    onClick={() => {
                      setPostType(value);
                      setShowTypeSelector(false);
                      if (capturedMedia || uploadedUrl) {
                        setStep('compose');
                        setShowComposer(true);
                      } else {
                        setShowMediaPicker(true);
                      }
                    }}
                  >
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
                {POST_TYPES.filter(pt => CONNECTION_POST_TYPES.has(pt.value)).map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md hover-elevate text-left w-full"
                    onClick={() => {
                      setPostType(value);
                      setShowTypeSelector(false);
                      if (capturedMedia || uploadedUrl) {
                        setStep('compose');
                        setShowComposer(true);
                      } else {
                        setShowMediaPicker(true);
                      }
                    }}
                  >
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium">{label}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">Video friendly</Badge>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showMediaPicker && (
        <MediaPickerScreen
          onCapture={handleMediaPickerCapture}
          onSkip={handleMediaPickerSkip}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      <Dialog open={showComposer} onOpenChange={(open) => !open && closeAndReset()}>
        <DialogContent hideCloseButton className="max-w-lg w-full p-0 gap-0 overflow-hidden h-[90svh] flex flex-col">
          <ComposeHeader
            step={step}
            currentStepIndex={currentStepIndex}
            onClose={closeAndReset}
            isCarouselMode={isCarouselMode}
            onBack={
              step === 'carousel-picker'
                ? () => { closeAndReset(); setShowTypeSelector(true); }
                : step === 'carousel-reorder'
                ? () => setStep('carousel-picker')
                : step === 'distribute' && isCarouselMode
                ? () => setStep('carousel-reorder')
                : step === 'edit' || step === 'menu-item' || (step === 'compose' && videoSkippedMenuStep)
                ? () => { setShowComposer(false); setShowMediaPicker(true); setCapturedMedia(null); setUploadedUrl(null); setVideoSkippedMenuStep(false); }
                : () => setStep(STEPS[currentStepIndex - 1])
            }
            quickMode={isCarouselMode ? undefined : quickMode}
            onToggleMode={isCarouselMode ? undefined : () => setQuickMode(prev => !prev)}
            postType={step === 'compose' ? postType : undefined}
            onChangeType={step === 'compose' ? () => { setShowComposer(false); setShowTypeSelector(true); } : undefined}
          />

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {step === 'edit' && capturedMedia && capturedMedia.type !== 'video' && (
              <ImageEditorStep
                capturedMedia={capturedMedia}
                onDone={handleEditorDone}
              />
            )}

            {step === 'menu-item' && (
              <MenuItemStep
                menuData={menuData}
                menuLoading={menuLoading}
                search={menuSearch}
                onSearch={setMenuSearch}
                filteredItems={filteredItems}
                selectedItem={selectedItem}
                onSelect={(item) => {
                  const newItem = selectedItem?.id === item.id ? null : item;
                  setSelectedItem(newItem);
                  if (!captionManuallyEdited) {
                    setCaption(generateSuggestedCaption(postType, newItem, restaurantName));
                  }
                }}
                capturedMedia={capturedMedia}
                uploadedUrl={uploadedUrl}
                onNext={() => {
                  if (!caption.trim() && !captionManuallyEdited) {
                    setCaption(generateSuggestedCaption(postType, selectedItem, restaurantName));
                  }
                  if (quickMode && caption.trim()) {
                    setStep('distribute');
                  } else {
                    setStep('compose');
                  }
                }}
                restaurantId={restaurantId}
                eatzyToken={eatzyToken}
                authToken={authToken}
                onItemCreated={(newItem) => {
                  queryClient.setQueryData<MenuData>(
                    ['/api/partner/menu', restaurantProfile],
                    (prev) => {
                      if (!prev) return prev;
                      const catIdx = prev.categories.findIndex(c => c.id === newItem.categoryId);
                      if (catIdx === -1) return prev;
                      const updatedCats = prev.categories.map((cat, i) =>
                        i === catIdx ? { ...cat, items: [...cat.items, newItem] } : cat
                      );
                      return {
                        ...prev,
                        categories: updatedCats,
                        itemMenuMap: newItem.id
                          ? { ...prev.itemMenuMap, [newItem.id]: newItem.menuId }
                          : prev.itemMenuMap,
                      };
                    }
                  );
                }}
              />
            )}

            {step === 'compose' && (
              <ComposeStep
                caption={caption}
                onCaptionChange={(val) => { setCaption(val); setCaptionManuallyEdited(true); }}
                postType={postType}
                offerEnabled={offerEnabled}
                onOfferToggle={(enabled) => {
                  setOfferEnabled(enabled);
                  if (enabled) {
                    // Apply smart defaults the first time the offer is enabled
                    // (when state is still at reset values). Preserve any existing values.
                    if (!couponCode) setCouponCode(generateCouponCode());
                    if (!couponDiscountType) setCouponDiscountType('10%_off');
                    if (!offerDuration) setOfferDuration('today');
                    // offerType already defaults to 'coupon' in useState
                  }
                }}
                offerType={offerType}
                onOfferTypeChange={(t) => {
                  setOfferType(t);
                  if (t === 'coupon' && !couponCode) setCouponCode(generateCouponCode());
                  if (t === 'kudoz' && !kudozBonusType) setKudozBonusType('2x');
                }}
                couponCode={couponCode}
                onRegenerateCouponCode={() => setCouponCode(generateCouponCode())}
                couponDiscountType={couponDiscountType}
                onCouponDiscountTypeChange={(d) => { setCouponDiscountType(d); setSelectedExistingCouponId(null); setSelectedExistingCouponCode(''); }}
                offerDuration={offerDuration}
                onOfferDurationChange={setOfferDuration}
                kudozBonusType={kudozBonusType}
                onKudozBonusTypeChange={setKudozBonusType}
                existingCoupons={existingCoupons}
                selectedExistingCouponId={selectedExistingCouponId}
                selectedExistingCouponCode={selectedExistingCouponCode}
                onSelectExistingCoupon={(id, code) => { setSelectedExistingCouponId(id || null); setSelectedExistingCouponCode(code); if (id) setCouponDiscountType(null); }}
                walletBalance={walletBalance}
                previousKudozBonuses={previousKudozBonuses}
                selectedItem={selectedItem}
                capturedMedia={capturedMedia}
                uploadedUrl={uploadedUrl}
                canAdvance={canAdvanceFromCompose}
                onNext={() => setStep('distribute')}
                onRefreshCaption={() => {
                  setCaption(generateSuggestedCaption(postType, selectedItem, restaurantName));
                  setCaptionManuallyEdited(false);
                }}
                isConnectionType={CONNECTION_POST_TYPES.has(postType)}
                captionStarters={CAPTION_STARTERS[postType] || []}
                isVideo={capturedMedia?.type === 'video'}
                onTagDish={() => { setVideoSkippedMenuStep(false); setStep('menu-item'); }}
              />
            )}

            {step === 'carousel-picker' && (
              <CarouselPickerStep
                menuData={menuData}
                menuLoading={menuLoading}
                restaurantName={restaurantName}
                restaurantId={restaurantId}
                eatzyToken={eatzyToken}
                authToken={authToken}
                uploadFile={uploadFile}
                onNext={(picked) => {
                  setCarouselItems(picked.map(item => ({
                    id: item.id,
                    name: item.name,
                    image: item.image!,
                    price: item.price,
                    caption: item.description ? `${item.name} — ${item.description.slice(0, 60)}` : item.name,
                  })));
                  setCarouselOverallCaption(`Today's specials at ${restaurantName} — ${picked.map(i => i.name).join(', ')}`);
                  setStep('carousel-reorder');
                }}
              />
            )}

            {step === 'carousel-reorder' && (
              <CarouselReorderStep
                items={carouselItems}
                overallCaption={carouselOverallCaption}
                onItemsChange={setCarouselItems}
                onOverallCaptionChange={setCarouselOverallCaption}
                onNext={() => {
                  setCaption(carouselOverallCaption);
                  setCrossPostPlatforms(prev => {
                    const hasIg = prev.includes('instagram');
                    const hasFb = prev.includes('facebook');
                    let next = prev.filter(p => p !== 'instagram_story');
                    if (!hasIg && activeSocialPlatforms.find(p => p.platform === 'instagram')) next = [...next, 'instagram'];
                    if (!hasFb && activeSocialPlatforms.find(p => p.platform === 'facebook')) next = [...next, 'facebook'];
                    return next;
                  });
                  setStep('distribute');
                }}
              />
            )}

            {step === 'distribute' && (
              <DistributeStep
                caption={caption}
                postType={postType}
                restaurantName={restaurantName}
                uploadedUrl={uploadedUrl}
                capturedMedia={capturedMedia}
                selectedItem={selectedItem}
                activeSocialPlatforms={activeSocialPlatforms}
                hasConnectedPlatforms={hasConnectedPlatforms}
                crossPostPlatforms={crossPostPlatforms}
                onTogglePlatform={togglePlatform}
                platformOverrides={platformOverrides}
                onPlatformOverrideChange={(platform, text) => {
                  setPlatformOverrides(prev => ({ ...prev, [platform]: text }));
                }}
                onPreviewsLoaded={(previews) => {
                  setPlatformOverrides(() => {
                    const next: Record<string, string> = {};
                    for (const [platform, data] of Object.entries(previews)) {
                      next[platform] = data.formattedPreview;
                    }
                    return next;
                  });
                }}
                onPlatformReset={(platform, originalText) => {
                  setPlatformOverrides(prev => ({ ...prev, [platform]: originalText }));
                }}
                orderingLink={orderingLink || undefined}
                offerEnabled={offerEnabled}
                offerType={offerType}
                couponCode={couponCode}
                couponDiscountType={couponDiscountType}
                kudozBonusType={kudozBonusType}
                offerDuration={offerDuration}
                selectedExistingCouponId={selectedExistingCouponId}
                selectedExistingCouponCode={selectedExistingCouponCode}
                igHashtagsInFirstComment={igHashtagsInFirstComment}
                onIgHashtagsInFirstCommentChange={setIgHashtagsInFirstComment}
                isPosting={createPostMutation.isPending}
                onPost={() => createPostMutation.mutate()}
                onEdit={() => isCarouselMode ? setStep('carousel-reorder') : setStep('compose')}
                isConnectionType={CONNECTION_POST_TYPES.has(postType)}
                isCarouselMode={isCarouselMode}
                carouselItems={carouselItems}
                savePhotoToMenu={savePhotoToMenu}
                onSavePhotoToMenuChange={setSavePhotoToMenu}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit existing post sheet */}
      <Sheet open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[90vh] flex flex-col">
          <SheetHeader className="pb-2">
            <SheetTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit Post
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-4 pb-6">
              {editingPost?.images?.[0] && (
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-muted">
                  <img
                    src={editingPost.images[0]}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Post type</Label>
                <Select value={editPostType} onValueChange={setEditPostType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {POST_TYPES.map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>
                        {pt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="e.g. Chef's Special tonight"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Caption</Label>
                <Textarea
                  value={editCaption}
                  onChange={e => setEditCaption(e.target.value)}
                  placeholder="Write your caption…"
                  className="min-h-[120px] resize-none"
                />
                <p className="text-xs text-muted-foreground text-right">{editCaption.length} characters</p>
              </div>
              <Button
                className="w-full"
                onClick={() => updatePostMutation.mutate()}
                disabled={!editCaption.trim() || updatePostMutation.isPending}
              >
                {updatePostMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  <><Check className="h-4 w-4 mr-2" />Save changes</>
                )}
              </Button>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </PartnerLayout>
  );
}

function ComposeHeader({
  step, currentStepIndex, onClose, onBack, quickMode, onToggleMode, isCarouselMode,
  postType, onChangeType,
}: {
  step: Step;
  currentStepIndex: number;
  onClose: () => void;
  onBack?: () => void;
  quickMode?: boolean;
  onToggleMode?: () => void;
  isCarouselMode?: boolean;
  postType?: string;
  onChangeType?: () => void;
}) {
  const resolvedType = postType ? resolvePostType(postType) : null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
      {onBack ? (
        <Button size="icon" variant="ghost" onClick={onBack} data-testid="button-compose-back">
          <ChevronLeft className="h-5 w-5" />
        </Button>
      ) : (
        <div className="w-9" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {isCarouselMode ? (
            (['carousel-picker', 'carousel-reorder', 'distribute'] as const).map((s, i) => {
              const carouselOrder = ['carousel-picker', 'carousel-reorder', 'distribute'];
              const currentIdx = carouselOrder.indexOf(step);
              const sIdx = carouselOrder.indexOf(s);
              return (
                <div key={s} className="flex items-center gap-1.5">
                  <div className={cn('w-2 h-2 rounded-full transition-colors', sIdx <= currentIdx ? 'bg-primary' : 'bg-muted-foreground/30')} />
                  {i < 2 && <div className={cn('h-px w-4', sIdx < currentIdx ? 'bg-primary' : 'bg-muted-foreground/30')} />}
                </div>
              );
            })
          ) : (
            STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1.5">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full transition-colors',
                    i < currentStepIndex ? 'bg-primary' :
                    i === currentStepIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                  )}
                />
                {i < 3 && <div className={cn('h-px w-4', i < currentStepIndex ? 'bg-primary' : 'bg-muted-foreground/30')} />}
              </div>
            ))
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <p className="text-sm font-semibold">{stepLabel(step)}</p>
          {onToggleMode && (
            <button
              onClick={onToggleMode}
              className="text-xs text-primary hover:underline underline-offset-2"
              data-testid="button-toggle-mode"
            >
              {quickMode ? 'More options' : 'Quick mode'}
            </button>
          )}
          {resolvedType && onChangeType && (
            <div className="flex items-center gap-1">
              <resolvedType.icon className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground truncate">{resolvedType.label}</span>
              <button
                type="button"
                onClick={onChangeType}
                className="text-xs text-primary underline underline-offset-2 shrink-0"
                data-testid="btn-change-type"
              >
                Change type
              </button>
            </div>
          )}
        </div>
      </div>
      <Button size="icon" variant="ghost" onClick={onClose} data-testid="button-compose-close">
        <X className="h-5 w-5" />
      </Button>
    </div>
  );
}

function MediaPickerScreen({
  onCapture,
  onSkip,
  onClose,
}: {
  onCapture: (media: CapturedMedia) => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  const [cameraOpen, setCameraOpen] = useState(false);

  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    if (isVideo && file.size > 100 * 1024 * 1024) {
      toast({
        title: 'Video too large',
        description: 'Please keep videos under 100 MB. Try trimming or compressing the file.',
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }
    onCapture({
      type: isVideo ? 'video' : 'photo',
      file,
      url: URL.createObjectURL(file),
    });
    e.target.value = '';
  };

  return (
    <>
      {cameraOpen && createPortal(
        <CameraFirstCapture
          isOpen={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(media) => {
            setCameraOpen(false);
            onCapture(media);
          }}
        />,
        document.body
      )}

      <div className="fixed inset-0 z-[9999] flex flex-col bg-background">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b">
          <div className="w-16" />
          <span className="font-semibold text-base">New Post</span>
          <button
            onClick={onClose}
            className="w-16 text-right text-sm text-muted-foreground"
          >
            Cancel
          </button>
        </div>

        <div className="flex flex-col items-center justify-center flex-1 gap-6 px-8 pb-10">
          <div className="rounded-full bg-primary/10 p-8">
            <ImageIcon className="h-14 w-14 text-primary" />
          </div>

          <div className="text-center space-y-1">
            <p className="font-semibold text-xl">Share your food</p>
            <p className="text-sm text-muted-foreground">Great photos drive 3× more orders</p>
          </div>

          <div className="flex flex-col w-full max-w-xs gap-3 mt-2">
            <label
              htmlFor="media-picker-gallery-input"
              className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-md bg-primary text-primary-foreground font-medium text-sm cursor-pointer px-4"
            >
              <ImageIcon className="h-5 w-5" />
              Choose from Gallery
            </label>
            <input
              id="media-picker-gallery-input"
              type="file"
              accept="image/*,video/*"
              className="sr-only"
              onChange={handleFileChange}
            />

            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={() => setCameraOpen(true)}
              data-testid="button-open-camera"
            >
              <Camera className="h-5 w-5 mr-2" />
              Open Camera
            </Button>

            <button
              onClick={onSkip}
              className="text-sm text-muted-foreground underline-offset-4 hover:underline mt-1"
              data-testid="button-skip-media"
            >
              Skip — text-only post
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function CameraStep({
  onCapture, onSkip, isUploading, autoOpen,
}: {
  onCapture: (media: CapturedMedia) => void;
  onSkip: () => void;
  isUploading: boolean;
  autoOpen?: boolean;
}) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const hasAutoOpened = useRef(false);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  if (autoOpen && !hasAutoOpened.current && !isUploading && !cameraOpen) {
    hasAutoOpened.current = true;
    setTimeout(() => setCameraOpen(true), 100);
  }

  const handleGallery = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const media: CapturedMedia = {
      type: file.type.startsWith('video/') ? 'video' : 'photo',
      file,
      url,
    };
    onCapture(media);
  }, [onCapture]);

  return (
    <div className="flex flex-col flex-1">
      {cameraOpen && createPortal(
        <CameraFirstCapture
          isOpen={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(media) => {
            setCameraOpen(false);
            onCapture(media);
          }}
        />,
        document.body
      )}
      {(
        <div className="flex flex-col items-center justify-center gap-5 p-8 h-full min-h-[360px]">
          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Uploading media...</p>
            </div>
          ) : (
            <>
              <div className="rounded-full bg-primary/10 p-6">
                <Camera className="h-12 w-12 text-primary" />
              </div>
              <div className="text-center space-y-1">
                <p className="font-semibold text-lg">Snap your food</p>
                <p className="text-sm text-muted-foreground">Great food photos drive 3x more orders</p>
              </div>
              <div className="flex flex-col w-full max-w-xs gap-3">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => setCameraOpen(true)}
                  data-testid="button-open-camera"
                >
                  <Camera className="h-5 w-5 mr-2" />
                  Open Camera
                </Button>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="sr-only"
                  onChange={handleGallery}
                />
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full"
                  data-testid="button-open-gallery"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <ImageIcon className="h-5 w-5 mr-2" />
                  Choose from Gallery
                </Button>
                <button
                  onClick={onSkip}
                  className="text-sm text-muted-foreground underline-offset-4 hover:underline mt-1"
                  data-testid="button-skip-media"
                >
                  Skip — text-only post
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const ASPECT_RATIOS = { '1:1': 1, '4:5': 4 / 5, '16:9': 16 / 9 } as const;
type AspectKey = keyof typeof ASPECT_RATIOS;

function ImageEditorStep({
  capturedMedia,
  onDone,
}: {
  capturedMedia: CapturedMedia;
  onDone: (croppedDataUrl: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectKey>('1:1');

  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [displayScale, setDisplayScale] = useState(1);
  const [displayOffset, setDisplayOffset] = useState({ x: 0, y: 0 });

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const lastPinchDist = useRef<number | null>(null);
  const dragAnchor = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const getContainerDims = () => {
    if (!containerRef.current) return { w: 300, h: 300 };
    const r = containerRef.current.getBoundingClientRect();
    return { w: r.width, h: r.height };
  };

  const clamp = useCallback((ox: number, oy: number, s: number) => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return { x: ox, y: oy };
    const { w, h } = getContainerDims();
    const cover = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const rW = img.naturalWidth * cover * s;
    const rH = img.naturalHeight * cover * s;
    const maxX = Math.max(0, (rW - w) / 2);
    const maxY = Math.max(0, (rH - h) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, ox)), y: Math.min(maxY, Math.max(-maxY, oy)) };
  }, []);

  const applyTransform = useCallback((s: number, ox: number, oy: number) => {
    const c = clamp(ox, oy, s);
    scaleRef.current = s;
    offsetRef.current = c;
    setDisplayScale(s);
    setDisplayOffset(c);
  }, [clamp]);

  const resetTransform = useCallback(() => applyTransform(1, 0, 0), [applyTransform]);

  useEffect(() => { resetTransform(); }, [aspectRatio, resetTransform]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      dragAnchor.current = { x: e.clientX, y: e.clientY, ox: offsetRef.current.x, oy: offsetRef.current.y };
    } else if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      lastPinchDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = Array.from(pointers.current.values());
    if (pts.length === 1) {
      const dx = e.clientX - dragAnchor.current.x;
      const dy = e.clientY - dragAnchor.current.y;
      applyTransform(scaleRef.current, dragAnchor.current.ox + dx, dragAnchor.current.oy + dy);
    } else if (pts.length === 2) {
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (lastPinchDist.current !== null) {
        const factor = dist / lastPinchDist.current;
        const newScale = Math.min(Math.max(scaleRef.current * factor, 1), 5);
        applyTransform(newScale, offsetRef.current.x, offsetRef.current.y);
      }
      lastPinchDist.current = dist;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    lastPinchDist.current = null;
    if (pointers.current.size === 1) {
      const [pt] = Array.from(pointers.current.values());
      dragAnchor.current = { x: pt.x, y: pt.y, ox: offsetRef.current.x, oy: offsetRef.current.y };
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    applyTransform(Math.min(Math.max(scaleRef.current * factor, 1), 5), offsetRef.current.x, offsetRef.current.y);
  };

  const zoomBy = (factor: number) => {
    applyTransform(Math.min(Math.max(scaleRef.current * factor, 1), 5), offsetRef.current.x, offsetRef.current.y);
  };

  const handleDone = () => {
    const img = imgRef.current;
    if (!img) return;
    const { w: cW, h: cH } = getContainerDims();
    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    const cover = Math.max(cW / nW, cH / nH);
    const rW = nW * cover * scaleRef.current;
    const rH = nH * cover * scaleRef.current;
    const imgLeft = cW / 2 + offsetRef.current.x - rW / 2;
    const imgTop = cH / 2 + offsetRef.current.y - rH / 2;
    const srcX = (-imgLeft / rW) * nW;
    const srcY = (-imgTop / rH) * nH;
    const srcW = (cW / rW) * nW;
    const srcH = (cH / rH) * nH;
    const ratio = ASPECT_RATIOS[aspectRatio];
    const outW = ratio >= 1 ? 1080 : Math.round(1080 * ratio);
    const outH = ratio >= 1 ? Math.round(1080 / ratio) : 1080;
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
    onDone(canvas.toDataURL('image/jpeg', 0.92));
  };

  return (
    <div className="flex flex-col h-full bg-black">
      <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden">
        <div
          ref={containerRef}
          className="relative overflow-hidden select-none"
          style={{
            width: '100%',
            aspectRatio: String(ASPECT_RATIOS[aspectRatio]),
            maxHeight: '100%',
            cursor: 'grab',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        >
          <img
            ref={imgRef}
            src={capturedMedia.url}
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{
              transform: `translate(${displayOffset.x}px, ${displayOffset.y}px) scale(${displayScale})`,
              transformOrigin: 'center center',
              userSelect: 'none',
            }}
          />
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <line x1="33.33%" y1="0" x2="33.33%" y2="100%" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
            <line x1="66.66%" y1="0" x2="66.66%" y2="100%" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
            <line x1="0" y1="33.33%" x2="100%" y2="33.33%" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
            <line x1="0" y1="66.66%" x2="100%" y2="66.66%" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
          </svg>
        </div>
      </div>

      <div className="shrink-0 bg-zinc-900 px-3 py-2.5 flex items-center gap-2">
        <div className="flex gap-1">
          {(Object.keys(ASPECT_RATIOS) as AspectKey[]).map(ar => (
            <button
              key={ar}
              onClick={() => setAspectRatio(ar)}
              className={cn(
                'text-xs px-2 py-1 rounded-md font-medium transition-colors',
                aspectRatio === ar ? 'bg-white text-black' : 'text-zinc-400 hover:text-zinc-200'
              )}
            >
              {ar}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-700"
          onClick={() => zoomBy(0.833)}
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-700"
          onClick={resetTransform}
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-700"
          onClick={() => zoomBy(1.2)}
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button size="sm" onClick={handleDone} className="ml-1">
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function MenuItemStep({
  menuData, menuLoading, search, onSearch, filteredItems,
  selectedItem, onSelect, capturedMedia, uploadedUrl, onNext,
  restaurantId, eatzyToken, authToken, onItemCreated,
}: {
  menuData: MenuData | undefined;
  menuLoading: boolean;
  search: string;
  onSearch: (s: string) => void;
  filteredItems: MenuItem[];
  selectedItem: MenuItem | null;
  onSelect: (item: MenuItem) => void;
  capturedMedia: CapturedMedia | null;
  uploadedUrl: string | null;
  onNext: () => void;
  restaurantId: string | null;
  eatzyToken: string | null;
  authToken: string | null;
  onItemCreated?: (item: MenuItem) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPrice, setCreatePrice] = useState('');
  const [createCategoryId, setCreateCategoryId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setCreateName(search.trim());
    setCreatePrice('');
    const firstCat = menuData?.categories?.[0];
    setCreateCategoryId(firstCat ? String(firstCat.id) : '');
    setCreateError(null);
    setShowCreate(true);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) { setCreateError('Name is required'); return; }
    const priceNum = parseFloat(createPrice);
    if (isNaN(priceNum) || priceNum < 0) { setCreateError('Enter a valid price'); return; }
    if (!createCategoryId) { setCreateError('Select a category'); return; }

    const selectedCat = menuData?.categories?.find(c => String(c.id) === createCategoryId);
    const menuId = selectedCat?.menuId
      ?? menuData?.categories?.[0]?.items?.[0]?.menuId
      ?? Object.values(menuData?.itemMenuMap ?? {})[0];

    if (!menuId || !restaurantId) {
      setCreateError('Cannot determine menu. Please refresh and try again.');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const res = await fetch(
        `/api/partner/restmanager/${restaurantId}/menus/${menuId}/items`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
            'X-Eatzy-Token': eatzyToken || '',
          },
          body: JSON.stringify({
            name: createName.trim(),
            price: priceNum,
            categoryId: createCategoryId,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data.error || data.message || 'Failed to create dish');
        return;
      }
      const rawItem = data.item ?? {};
      const newItem: MenuItem = {
        id: rawItem.id ?? `temp-${Date.now()}`,
        name: rawItem.name || createName.trim(),
        description: rawItem.description || '',
        price: rawItem.price ?? priceNum,
        image: rawItem.image || undefined,
        available: rawItem.available !== false,
        menuId: rawItem.menuId ?? Number(menuId),
        categoryId: rawItem.categoryId ?? Number(createCategoryId),
      };
      setShowCreate(false);
      onItemCreated?.(newItem);
      onSelect(newItem);
    } catch {
      setCreateError('Network error. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const categories = menuData?.categories ?? [];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-4 space-y-3 shrink-0">
        {capturedMedia && (
          <div className="relative w-full h-20 rounded-lg overflow-hidden bg-muted">
            {capturedMedia.type === 'video' ? (
              <video src={capturedMedia.url} className="w-full h-full object-cover" muted playsInline />
            ) : (
              <img src={capturedMedia.url} alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute top-2 right-2">
              {uploadedUrl ? (
                <Badge className="bg-green-600 text-white gap-1">
                  <Check className="h-3 w-3" /> Uploaded
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading
                </Badge>
              )}
            </div>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search menu items…"
            value={search}
            onChange={e => { onSearch(e.target.value); setShowCreate(false); }}
            data-testid="input-menu-search"
          />
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 px-4">
        {menuLoading ? (
          <div className="space-y-2 pb-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center pb-4 text-muted-foreground gap-2 pt-6">
            <Utensils className="h-8 w-8" />
            <p className="text-sm">{search ? 'No items match your search' : 'No menu items found'}</p>
            {search.trim() && categories.length > 0 && !showCreate && (
              <Button
                variant="outline"
                size="sm"
                className="mt-1 gap-1.5"
                onClick={handleOpenCreate}
                data-testid="button-create-dish"
              >
                <Plus className="h-3.5 w-3.5" />
                Create &ldquo;{search.trim()}&rdquo; as new dish
              </Button>
            )}
            {showCreate && (
              <form
                onSubmit={handleCreate}
                className="w-full mt-2 rounded-lg border border-border bg-muted/40 p-4 space-y-3 text-left"
                data-testid="form-create-dish"
              >
                <p className="text-sm font-medium text-foreground">New dish</p>
                <div className="space-y-1">
                  <Label htmlFor="create-name" className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    id="create-name"
                    value={createName}
                    onChange={e => setCreateName(e.target.value)}
                    placeholder="Dish name"
                    data-testid="input-create-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="create-price" className="text-xs text-muted-foreground">Price ($)</Label>
                  <Input
                    id="create-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={createPrice}
                    onChange={e => setCreatePrice(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-create-price"
                  />
                </div>
                {categories.length > 0 && (
                  <div className="space-y-1">
                    <Label htmlFor="create-category" className="text-xs text-muted-foreground">Category</Label>
                    <Select value={createCategoryId} onValueChange={setCreateCategoryId}>
                      <SelectTrigger id="create-category" data-testid="select-create-category">
                        <SelectValue placeholder="Pick a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {createError && (
                  <p className="text-xs text-destructive" data-testid="error-create-dish">{createError}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreate(false)}
                    disabled={creating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={creating}
                    data-testid="button-submit-create-dish"
                  >
                    {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                    {creating ? 'Creating…' : 'Create & select'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div className="space-y-2 pb-4">
            {filteredItems.map(item => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <button
                  key={item.id}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover-elevate',
                    isSelected ? 'border-primary bg-primary/5' : 'border-border'
                  )}
                  onClick={() => onSelect(item)}
                  data-testid={`menu-item-${item.id}`}
                >
                  {item.image ? (
                    <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="shrink-0 w-12 h-12 rounded-md bg-muted flex items-center justify-center">
                      <Utensils className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm leading-tight truncate">{item.name}</p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{item.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      ${item.price.toFixed(2)}
                    </p>
                  </div>
                  <div className={cn(
                    'shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                  )}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <div className="p-4 border-t shrink-0 flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {selectedItem ? (
            <span className="flex items-center gap-1.5 text-primary font-medium">
              <Tag className="h-3.5 w-3.5" />
              {selectedItem.name}
            </span>
          ) : (
            'No item selected'
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onNext} data-testid="button-skip-menu-item">
            Skip
          </Button>
          <Button onClick={onNext} data-testid="button-next-to-compose">
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

const DURATION_CHIPS: { value: 'today' | 'one_week' | '30_days'; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'one_week', label: '1 week' },
  { value: '30_days', label: '30 days' },
];

const DISCOUNT_OPTIONS: { value: '$5_off' | '10%_off' | 'free_delivery'; label: string; icon: React.ElementType }[] = [
  { value: '$5_off', label: '$5 off', icon: DollarSign },
  { value: '10%_off', label: '10% off', icon: Percent },
  { value: 'free_delivery', label: 'Free delivery', icon: Truck },
];

const KUDOZ_PRESETS: { value: '2x' | '50' | '100' | '200'; label: string }[] = [
  { value: '2x', label: '2× Kudoz' },
  { value: '50', label: '+50 Kudoz' },
  { value: '100', label: '+100 Kudoz' },
  { value: '200', label: '+200 Kudoz' },
];

function ComposeStep({
  caption, onCaptionChange, postType,
  offerEnabled, onOfferToggle, offerType, onOfferTypeChange,
  couponCode, onRegenerateCouponCode,
  couponDiscountType, onCouponDiscountTypeChange,
  offerDuration, onOfferDurationChange,
  kudozBonusType, onKudozBonusTypeChange,
  existingCoupons, selectedExistingCouponId, selectedExistingCouponCode, onSelectExistingCoupon,
  walletBalance, previousKudozBonuses,
  selectedItem, capturedMedia, uploadedUrl,
  canAdvance, onNext, onRefreshCaption,
  isConnectionType, captionStarters, isVideo, onTagDish,
}: {
  caption: string;
  onCaptionChange: (s: string) => void;
  postType: string;
  offerEnabled: boolean;
  onOfferToggle: (b: boolean) => void;
  offerType: 'coupon' | 'kudoz';
  onOfferTypeChange: (t: 'coupon' | 'kudoz') => void;
  couponCode: string;
  onRegenerateCouponCode: () => void;
  couponDiscountType: '$5_off' | '10%_off' | 'free_delivery' | null;
  onCouponDiscountTypeChange: (d: '$5_off' | '10%_off' | 'free_delivery') => void;
  offerDuration: 'today' | 'one_week' | '30_days' | null;
  onOfferDurationChange: (d: 'today' | 'one_week' | '30_days') => void;
  kudozBonusType: string | null;
  onKudozBonusTypeChange: (v: string) => void;
  existingCoupons: any[];
  selectedExistingCouponId: number | null;
  selectedExistingCouponCode: string;
  onSelectExistingCoupon: (id: number, code: string) => void;
  walletBalance: number | null;
  previousKudozBonuses: number[];
  selectedItem: MenuItem | null;
  capturedMedia: CapturedMedia | null;
  uploadedUrl: string | null;
  canAdvance: boolean;
  onNext: () => void;
  onRefreshCaption: () => void;
  isConnectionType: boolean;
  captionStarters: string[];
  isVideo: boolean;
  onTagDish: () => void;
}) {
  const walletEmpty = walletBalance === 0;
  const walletLoading = walletBalance === null;

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full overflow-hidden">
      <ScrollArea className="flex-1 min-h-0 w-full">
        <div className="p-4 pr-5 space-y-5 w-full overflow-x-hidden">
          {capturedMedia && (
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted shrink-0">
              {capturedMedia.type === 'video' ? (
                <div className="relative w-full h-full">
                  <video src={capturedMedia.url} className="w-full h-full object-cover" muted playsInline />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-black/50 p-2">
                      <Video className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
              ) : (
                <img src={capturedMedia.url} alt="" className="w-full h-full object-cover" />
              )}
            </div>
          )}

          {selectedItem && (
            <div className="flex items-center gap-2 px-3 py-2 bg-primary/8 rounded-lg border border-primary/20">
              <Tag className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primary truncate">{selectedItem.name}</p>
                {selectedItem.description && (
                  <p className="text-xs text-muted-foreground truncate">{selectedItem.description}</p>
                )}
              </div>
            </div>
          )}

          {isConnectionType && !selectedItem && isVideo && (
            <button
              onClick={onTagDish}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-muted-foreground hover-elevate text-sm"
              data-testid="button-tag-dish-optional"
            >
              <Tag className="h-4 w-4 shrink-0" />
              <span>Tag a dish (optional)</span>
            </button>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Caption *</Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs gap-1 text-muted-foreground"
                onClick={onRefreshCaption}
                data-testid="button-refresh-caption"
              >
                <RefreshCw className="h-3 w-3" />
                New suggestion
              </Button>
            </div>

            {isConnectionType && captionStarters.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="caption-starters">
                {captionStarters.map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => onCaptionChange(caption ? starter + ' ' + caption : starter + ' ')}
                    className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/60 text-muted-foreground hover-elevate truncate max-w-[180px]"
                    title={starter}
                    data-testid="caption-starter-chip"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            )}

            <Textarea
              placeholder={isConnectionType ? "Share what's happening in your own words…" : "Tell customers about this dish, special, or news…"}
              value={caption}
              onChange={e => onCaptionChange(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="input-caption"
            />
            <p className="text-xs text-muted-foreground text-right">{caption.length} chars</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-muted-foreground" />
                <Label className="cursor-pointer" htmlFor="offer-toggle">Attach an offer</Label>
              </div>
              <Switch
                id="offer-toggle"
                checked={offerEnabled}
                onCheckedChange={onOfferToggle}
                data-testid="switch-offer"
              />
            </div>

            {offerEnabled && (
              <div className="space-y-4 p-3 rounded-lg bg-muted/50 border">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onOfferTypeChange('coupon')}
                    className={cn(
                      'flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors hover-elevate',
                      offerType === 'coupon' ? 'border-primary bg-primary/8 text-primary' : 'border-border'
                    )}
                    data-testid="offer-type-coupon"
                  >
                    <Tag className="h-4 w-4" />
                    Coupon Code
                  </button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => !walletEmpty && onOfferTypeChange('kudoz')}
                          disabled={walletEmpty}
                          className={cn(
                            'flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors',
                            walletEmpty ? 'opacity-40 cursor-not-allowed border-border' : 'hover-elevate',
                            offerType === 'kudoz' && !walletEmpty ? 'border-primary bg-primary/8 text-primary' : 'border-border'
                          )}
                          data-testid="offer-type-kudoz"
                        >
                          <Zap className="h-4 w-4" />
                          Kudoz Bonus
                        </button>
                      </TooltipTrigger>
                      {walletEmpty && (
                        <TooltipContent side="top">Add Kudoz to your wallet first</TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {offerType === 'coupon' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-background border font-mono text-sm font-semibold tracking-wider flex-1 min-w-0 overflow-hidden">
                        <span className="truncate">{selectedExistingCouponId ? selectedExistingCouponCode : couponCode}</span>
                      </div>
                      {!selectedExistingCouponId && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0 gap-1.5 text-xs text-muted-foreground"
                          onClick={onRegenerateCouponCode}
                          data-testid="button-regenerate-code"
                        >
                          <RefreshCw className="h-3 w-3" />
                          New code
                        </Button>
                      )}
                      {selectedExistingCouponId && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0 gap-1.5 text-xs text-muted-foreground"
                          onClick={() => onSelectExistingCoupon(0, '')}
                          data-testid="button-clear-existing-coupon"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Use new
                        </Button>
                      )}
                    </div>

                    {!selectedExistingCouponId && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Discount</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {DISCOUNT_OPTIONS.map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              onClick={() => onCouponDiscountTypeChange(value)}
                              className={cn(
                                'flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors hover-elevate',
                                couponDiscountType === value ? 'border-primary bg-primary/8 text-primary' : 'border-border text-foreground'
                              )}
                              data-testid={`discount-${value}`}
                            >
                              <Icon className="h-4 w-4" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {existingCoupons.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Or use an existing coupon</Label>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {existingCoupons.slice(0, 6).map((c: any) => (
                            <button
                              key={c.id}
                              onClick={() => onSelectExistingCoupon(c.id, c.code)}
                              className={cn(
                                'w-full flex items-center justify-between px-3 py-1.5 rounded-md border text-sm transition-colors hover-elevate',
                                selectedExistingCouponId === c.id ? 'border-primary bg-primary/8 text-primary' : 'border-border'
                              )}
                              data-testid={`existing-coupon-${c.id}`}
                            >
                              <span className="font-mono font-semibold">{c.code}</span>
                              {c.name && <span className="text-xs text-muted-foreground truncate ml-2">{c.name}</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Duration *</Label>
                      <div className="flex gap-2">
                        {DURATION_CHIPS.map(({ value, label }) => (
                          <button
                            key={value}
                            onClick={() => onOfferDurationChange(value)}
                            className={cn(
                              'flex-1 py-1.5 rounded-lg border text-xs font-medium transition-colors hover-elevate',
                              offerDuration === value ? 'border-primary bg-primary/8 text-primary' : 'border-border text-foreground'
                            )}
                            data-testid={`duration-${value}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {offerType === 'kudoz' && (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Kudoz bonus</Label>
                        {walletEmpty && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1 text-xs text-destructive cursor-default">
                                  <Zap className="h-3 w-3" />
                                  Wallet empty
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                Add Kudoz to your wallet first
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {KUDOZ_PRESETS.map(({ value, label }) => {
                          const disabled = walletEmpty || walletLoading;
                          return (
                            <button
                              key={value}
                              onClick={() => !disabled && onKudozBonusTypeChange(value)}
                              disabled={disabled}
                              className={cn(
                                'py-2 rounded-lg border text-xs font-medium transition-colors',
                                disabled ? 'opacity-40 cursor-not-allowed border-border' : 'hover-elevate',
                                kudozBonusType === value && !disabled ? 'border-primary bg-primary/8 text-primary' : 'border-border text-foreground'
                              )}
                              data-testid={`kudoz-preset-${value}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {previousKudozBonuses.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Previous bonuses</Label>
                        <div className="flex flex-wrap gap-2">
                          {previousKudozBonuses.map(v => (
                            <button
                              key={v}
                              onClick={() => {
                                const asStr = String(v) as '50' | '100' | '200';
                                onKudozBonusTypeChange(asStr);
                              }}
                              className={cn(
                                'px-2.5 py-1 rounded-full border text-xs font-medium hover-elevate',
                                kudozBonusType === String(v) ? 'border-primary bg-primary/8 text-primary' : 'border-border text-foreground'
                              )}
                            >
                              +{v}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Duration *</Label>
                      <div className="flex gap-2">
                        {DURATION_CHIPS.map(({ value, label }) => (
                          <button
                            key={value}
                            onClick={() => onOfferDurationChange(value)}
                            className={cn(
                              'flex-1 py-1.5 rounded-lg border text-xs font-medium transition-colors hover-elevate',
                              offerDuration === value ? 'border-primary bg-primary/8 text-primary' : 'border-border text-foreground'
                            )}
                            data-testid={`duration-${value}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {walletBalance !== null && !walletEmpty && (
                      <p className="text-xs text-muted-foreground">
                        Wallet balance: <span className="font-semibold text-foreground">{walletBalance.toLocaleString()} Kudoz</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      <div className="p-4 border-t shrink-0">
        <Button
          className="w-full"
          size="lg"
          disabled={!canAdvance}
          onClick={onNext}
          data-testid="button-next-to-distribute"
        >
          Next: Post everywhere
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

type CarouselItemData = { id: string; name: string; image: string; price: number; caption: string };

function CarouselPickerStep({
  menuData, menuLoading, restaurantName, onNext,
  restaurantId, eatzyToken, authToken, uploadFile,
}: {
  menuData: any;
  menuLoading: boolean;
  restaurantName: string;
  onNext: (items: MenuItem[]) => void;
  restaurantId: string | null;
  eatzyToken: string | null;
  authToken: string | null;
  uploadFile: (file: File) => Promise<{ objectPath: string } | null>;
}) {
  const [selected, setSelected] = useState<MenuItem[]>([]);
  // Map of itemId → temporary image URL for items that got photos added inline
  const [tempImages, setTempImages] = useState<Record<string, string>>({});
  // Track which items have an in-progress inline upload
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  // Track items pending "Save to menu?" confirmation (itemId → tempUrl)
  const [pendingSave, setPendingSave] = useState<Record<string, string>>({});
  // Track items currently being saved to menu
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadItemRef = useRef<string | null>(null);

  const categories: { name: string; items: MenuItem[] }[] = useMemo(() => {
    if (!menuData?.categories) return [];
    return (menuData.categories as { name: string; items: MenuItem[] }[])
      .map(cat => ({ name: cat.name, items: cat.items || [] }))
      .filter(cat => cat.items.length > 0);
  }, [menuData]);

  // An item "has image" if it has a real image OR a temp image from inline upload
  const getEffectiveImage = (item: MenuItem) => tempImages[item.id] || item.image || null;

  const toggle = (item: MenuItem) => {
    const img = getEffectiveImage(item);
    if (!img) return;
    setSelected(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev.filter(i => i.id !== item.id);
      if (prev.length >= 8) return prev;
      // Use effective image (may be temp)
      return [...prev, { ...item, image: img }];
    });
  };

  const handleAddPhotoClick = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    pendingUploadItemRef.current = itemId;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const itemId = pendingUploadItemRef.current;
    if (!file || !itemId) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';
    setUploadingItemId(itemId);
    try {
      const result = await uploadFile(file);
      if (result?.objectPath) {
        setTempImages(prev => ({ ...prev, [itemId]: result.objectPath }));
        setPendingSave(prev => ({ ...prev, [itemId]: result.objectPath }));
      }
      // If result is null, uploadFile's onError already showed a toast
    } catch {
      // Upload error is handled by useUpload's onError callback
    } finally {
      setUploadingItemId(null);
      pendingUploadItemRef.current = null;
    }
  };

  const handleSaveToMenu = async (item: MenuItem, imageUrl: string) => {
    const menuId = menuData?.itemMenuMap?.[item.id];
    if (!menuId || !restaurantId) {
      setPendingSave(prev => { const n = { ...prev }; delete n[item.id]; return n; });
      return;
    }
    setSavingItemId(item.id);
    try {
      const et = eatzyToken || '';
      const at = authToken || '';
      await fetch(`/api/partner/restmanager/${restaurantId}/menus/${menuId}/items/${item.id}/link-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': at ? `Bearer ${at}` : '',
          'X-Eatzy-Token': et,
        },
        body: JSON.stringify({ imageUrl }),
      });
    } catch { /* silent */ } finally {
      setSavingItemId(null);
      setPendingSave(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Hidden file input for inline photo capture */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <div className="px-4 pt-4 pb-2 shrink-0">
        <p className="text-sm text-muted-foreground">
          Pick <span className="font-semibold text-foreground">2–8 dishes</span> to feature in your carousel.
          Tap <Camera className="h-3 w-3 inline mb-0.5" /> to add a photo to any item.
        </p>
        {selected.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1">{selected.length} of 8 selected</p>
        )}
      </div>
      <ScrollArea className="flex-1 min-h-0">
        {menuLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <ImageIcon className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm font-medium">No menu items found</p>
            <p className="text-xs text-muted-foreground mt-1">Add items to your menu to create a carousel.</p>
          </div>
        ) : (
          <div className="p-4 space-y-5">
            {categories.map(cat => (
              <div key={cat.name}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{cat.name}</p>
                <div className="grid grid-cols-2 gap-3">
                  {cat.items.map(item => {
                    const effectiveImage = getEffectiveImage(item);
                    const hasImage = !!effectiveImage;
                    const isSelected = !!selected.find(i => i.id === item.id);
                    const selIdx = selected.findIndex(i => i.id === item.id);
                    const atMax = !isSelected && selected.length >= 8;
                    const isUploading = uploadingItemId === item.id;
                    const isSaving = savingItemId === item.id;
                    const hasPendingSave = !!pendingSave[item.id];
                    return (
                      <div key={item.id} className="flex flex-col gap-1">
                        {/* Items with a photo: use a <button> for selection */}
                        {hasImage ? (
                          <button
                            className={cn(
                              'relative rounded-lg overflow-hidden border-2 transition-all text-left w-full',
                              isSelected ? 'border-primary' : 'border-transparent',
                              !atMax ? 'hover-elevate' : 'opacity-40 cursor-not-allowed'
                            )}
                            onClick={() => !atMax && toggle(item)}
                            disabled={atMax}
                          >
                            <div className="aspect-square w-full bg-muted overflow-hidden">
                              <img src={effectiveImage!} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="p-2 bg-background">
                              <p className="text-xs font-medium leading-tight truncate">{item.name}</p>
                              <p className="text-xs text-muted-foreground">${(item.price / 100).toFixed(2)}</p>
                            </div>
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shadow">
                                {selIdx + 1}
                              </div>
                            )}
                            {isUploading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                              </div>
                            )}
                          </button>
                        ) : (
                          /* Items without a photo: use a <div> container so "Add photo" button is valid */
                          <div className="relative rounded-lg overflow-hidden border-2 border-transparent w-full">
                            <div className="aspect-square w-full bg-muted flex items-center justify-center">
                              <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
                            </div>
                            <div className="p-2 bg-background">
                              <p className="text-xs font-medium leading-tight truncate">{item.name}</p>
                              <p className="text-xs text-muted-foreground">${(item.price / 100).toFixed(2)}</p>
                            </div>
                            {/* Add photo button overlay — valid because parent is a div, not a button */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-muted/60">
                              <button
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs font-medium shadow-sm hover-elevate"
                                onClick={(e) => { e.stopPropagation(); handleAddPhotoClick(e, item.id); }}
                                disabled={isUploading}
                              >
                                {isUploading ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Camera className="h-3.5 w-3.5" />
                                )}
                                {isUploading ? 'Adding…' : 'Add photo'}
                              </button>
                            </div>
                          </div>
                        )}
                        {/* Inline "Save to menu?" prompt after photo is added */}
                        {hasPendingSave && (
                          <div className="flex items-center justify-between gap-1 px-1">
                            <p className="text-[10px] text-muted-foreground leading-tight flex-1">Save photo to menu?</p>
                            <div className="flex gap-1">
                              <button
                                className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover-elevate"
                                onClick={() => setPendingSave(prev => { const n = { ...prev }; delete n[item.id]; return n; })}
                                disabled={isSaving}
                              >
                                Skip
                              </button>
                              <button
                                className="text-[10px] px-1.5 py-0.5 rounded border border-primary/40 bg-primary/8 text-primary font-medium hover-elevate"
                                onClick={() => handleSaveToMenu(item, pendingSave[item.id])}
                                disabled={isSaving}
                              >
                                {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
      <div className="px-4 pb-4 pt-2 border-t shrink-0">
        <Button
          className="w-full"
          disabled={selected.length < 2}
          onClick={() => onNext(selected)}
        >
          Next: Arrange &amp; Caption
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function SortableCarouselCard({
  item, onCaptionChange,
}: {
  item: CarouselItemData;
  onCaptionChange: (id: string, caption: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.8 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="flex-shrink-0 w-44 flex flex-col gap-1.5">
      <div
        className="aspect-square w-full rounded-lg overflow-hidden border border-border relative bg-muted cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
          <p className="text-white text-xs font-semibold truncate">{item.name}</p>
          <p className="text-white/80 text-[10px]">${(item.price / 100).toFixed(2)}</p>
        </div>
        <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
          <GripVertical className="h-3 w-3 text-white" />
        </div>
      </div>
      <Textarea
        value={item.caption}
        onChange={e => onCaptionChange(item.id, e.target.value)}
        className="text-xs resize-none min-h-[56px] leading-snug"
        placeholder="Caption for this slide…"
        rows={3}
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

function CarouselReorderStep({
  items, overallCaption, onItemsChange, onOverallCaptionChange, onNext,
}: {
  items: CarouselItemData[];
  overallCaption: string;
  onItemsChange: (items: CarouselItemData[]) => void;
  onOverallCaptionChange: (caption: string) => void;
  onNext: () => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const ids = items.map(i => i.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIdx = items.findIndex(i => i.id === active.id);
      const newIdx = items.findIndex(i => i.id === over.id);
      onItemsChange(arrayMove(items, oldIdx, newIdx));
    }
  };

  const updateCaption = (id: string, caption: string) => {
    onItemsChange(items.map(i => i.id === id ? { ...i, caption } : i));
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-4 pt-3 pb-1 shrink-0">
        <p className="text-xs text-muted-foreground">Drag to reorder slides. Edit captions below each image.</p>
      </div>
      <div className="px-4 py-2 shrink-0">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {items.map(item => (
                <SortableCarouselCard key={item.id} item={item} onCaptionChange={updateCaption} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      <div className="px-4 py-3 border-t shrink-0 flex-1 flex flex-col gap-2">
        <Label className="text-xs font-medium text-muted-foreground">Overall post caption</Label>
        <Textarea
          value={overallCaption}
          onChange={e => onOverallCaptionChange(e.target.value)}
          className="resize-none text-sm"
          rows={4}
          placeholder="Main caption that appears with the carousel post…"
        />
      </div>
      <div className="px-4 pb-4 pt-1 shrink-0">
        <Button className="w-full" onClick={onNext}>
          Next: Post everywhere
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function CarouselPreview({
  items, caption, restaurantName, platform,
}: {
  items: CarouselItemData[];
  caption: string;
  restaurantName: string;
  platform?: 'instagram' | 'facebook';
}) {
  return (
    <div className={cn(
      'rounded-xl border overflow-hidden bg-background',
      platform === 'instagram' ? 'border-[#E4405F]/20' : platform === 'facebook' ? 'border-[#1877F2]/20' : 'border-border'
    )}>
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
          {restaurantName[0]?.toUpperCase()}
        </div>
        <span className="text-xs font-semibold truncate flex-1">{restaurantName}</span>
        {platform === 'instagram' && <SiInstagram className="h-3.5 w-3.5 text-[#E4405F] shrink-0" />}
        {platform === 'facebook' && <SiFacebook className="h-3.5 w-3.5 text-[#1877F2] shrink-0" />}
      </div>
      <div className="relative overflow-hidden">
        <div className="flex gap-1.5 overflow-x-auto pb-2 pt-2 px-2">
          {items.map((item, idx) => (
            <div key={item.id} className="flex-shrink-0 w-36 relative">
              <div className="aspect-square w-full rounded-md overflow-hidden bg-muted">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center">
                <span className="text-white text-[9px] font-bold">{idx + 1}</span>
              </div>
              {item.caption && (
                <p className="text-[10px] text-muted-foreground mt-1 leading-tight line-clamp-2 px-0.5">{item.caption}</p>
              )}
            </div>
          ))}
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-black/40 flex items-center justify-center pointer-events-none">
          <ChevronRight className="h-3 w-3 text-white" />
        </div>
      </div>
      <div className="px-3 pb-3">
        <p className="text-xs mt-1 leading-relaxed text-foreground line-clamp-3">{caption}</p>
        <p className="text-[10px] text-muted-foreground mt-1">{items.length} photos · Swipe to see all</p>
      </div>
    </div>
  );
}

function DistributeStep({
  caption, postType, restaurantName, uploadedUrl, capturedMedia,
  selectedItem, activeSocialPlatforms, hasConnectedPlatforms,
  crossPostPlatforms, onTogglePlatform,
  platformOverrides, onPlatformOverrideChange, onPreviewsLoaded, onPlatformReset,
  orderingLink,
  offerEnabled, offerType, couponCode, couponDiscountType, kudozBonusType, offerDuration, selectedExistingCouponId, selectedExistingCouponCode,
  igHashtagsInFirstComment, onIgHashtagsInFirstCommentChange,
  isPosting, onPost, onEdit, isConnectionType,
  isCarouselMode, carouselItems,
  savePhotoToMenu, onSavePhotoToMenuChange,
}: {
  caption: string;
  postType: string;
  restaurantName: string;
  uploadedUrl: string | null;
  capturedMedia: CapturedMedia | null;

  selectedItem: MenuItem | null;
  activeSocialPlatforms: SocialConnection[];
  hasConnectedPlatforms: boolean;
  crossPostPlatforms: string[];
  onTogglePlatform: (p: string) => void;
  platformOverrides: Record<string, string>;
  onPlatformOverrideChange: (platform: string, text: string) => void;
  onPreviewsLoaded: (previews: Record<string, { formattedPreview: string }>) => void;
  onPlatformReset: (platform: string, originalText: string) => void;
  orderingLink?: string;
  offerEnabled: boolean;
  offerType: 'coupon' | 'kudoz';
  couponCode: string;
  couponDiscountType: '$5_off' | '10%_off' | 'free_delivery' | null;
  kudozBonusType: string | null;
  offerDuration: 'today' | 'one_week' | '30_days' | null;
  selectedExistingCouponId: number | null;
  selectedExistingCouponCode: string;
  igHashtagsInFirstComment: boolean;
  onIgHashtagsInFirstCommentChange: (value: boolean) => void;
  isPosting: boolean;
  onPost: () => void;
  onEdit: () => void;
  isConnectionType: boolean;
  isCarouselMode?: boolean;
  carouselItems?: { id: string; name: string; image: string; price: number; caption: string }[];
  savePhotoToMenu?: boolean;
  onSavePhotoToMenuChange?: (val: boolean) => void;
}) {
  const selectedPostType = POST_TYPES.find(p => p.value === postType);
  const igConnection = activeSocialPlatforms.find(c => c.platform === 'instagram');
  const fbConnection = activeSocialPlatforms.find(c => c.platform === 'facebook');

  const isVideo = capturedMedia?.type === 'video';
  const mediaImageUrl = isVideo ? undefined : (uploadedUrl || undefined);
  const mediaVideoUrl = isVideo ? (uploadedUrl || capturedMedia?.url || undefined) : undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-5">
          <div className="rounded-lg bg-muted/50 border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Summary</p>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs gap-1 text-muted-foreground"
                onClick={onEdit}
              >
                <Pencil className="h-3 w-3" />
                Edit
              </Button>
            </div>
            <div className="flex items-start gap-3">
              {capturedMedia ? (
                <div className="shrink-0 w-14 h-14 rounded-md overflow-hidden bg-muted">
                  {capturedMedia.type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <Video className="h-5 w-5 text-muted-foreground" />
                    </div>
                  ) : (
                    <img src={capturedMedia.url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              ) : (
                <div className="shrink-0 w-14 h-14 rounded-md bg-muted flex items-center justify-center">
                  {selectedPostType && <selectedPostType.icon className="h-6 w-6 text-muted-foreground" />}
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedPostType && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <selectedPostType.icon className="h-3 w-3" />
                      {selectedPostType.label}
                    </Badge>
                  )}
                  {selectedItem && (
                    <Badge variant="outline" className="text-xs gap-1 text-primary border-primary/30">
                      <Tag className="h-3 w-3" />
                      {selectedItem.name}
                    </Badge>
                  )}
                  {offerEnabled && (
                    <Badge className="text-xs gap-1 bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                      {offerType === 'kudoz' ? (
                        <><Zap className="h-3 w-3" />{kudozBonusType === '2x' ? '2× Kudoz' : kudozBonusType ? `+${kudozBonusType} Kudoz` : 'Kudoz Bonus'}{offerDuration ? ` · ${offerDuration === 'today' ? 'Today only' : offerDuration === 'one_week' ? '1 week' : '30 days'}` : ''}</>
                      ) : selectedExistingCouponId ? (
                        <><Tag className="h-3 w-3" />{selectedExistingCouponCode || couponCode}{offerDuration ? ` · ${offerDuration === 'today' ? 'Today only' : offerDuration === 'one_week' ? '1 week' : '30 days'}` : ''}</>
                      ) : couponDiscountType ? (
                        <><Tag className="h-3 w-3" />{couponDiscountType === '$5_off' ? '$5 Off' : couponDiscountType === '10%_off' ? '10% Off' : 'Free Delivery'} · {couponCode}{offerDuration ? ` · ${offerDuration === 'today' ? 'Today only' : offerDuration === 'one_week' ? '1 week' : '30 days'}` : ''}</>
                      ) : (
                        <><Tag className="h-3 w-3" />{couponCode || 'Coupon'}</>
                      )}
                    </Badge>
                  )}
                </div>
                <p className="text-sm line-clamp-3 text-muted-foreground">{caption}</p>
              </div>
            </div>
            {/* Save photo to menu item opt-in */}
            {!isCarouselMode && selectedItem && uploadedUrl && !isVideo && onSavePhotoToMenuChange && (
              <div className="flex items-center gap-2.5 pt-1 border-t mt-1">
                <Checkbox
                  id="save-photo-to-menu"
                  checked={savePhotoToMenu}
                  onCheckedChange={(checked) => onSavePhotoToMenuChange(!!checked)}
                />
                <label
                  htmlFor="save-photo-to-menu"
                  className="text-xs text-muted-foreground leading-snug cursor-pointer select-none"
                >
                  Save this photo to <span className="font-medium text-foreground">{selectedItem.name}</span> on your menu
                </label>
              </div>
            )}
          </div>

          {isConnectionType && isVideo && (
            <div
              className="flex items-start gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5"
              data-testid="reels-nudge"
            >
              <div className="shrink-0 rounded-full bg-primary/10 p-1.5">
                <Video className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <p className="text-sm font-semibold text-primary leading-tight">Best for video: Reels + Facebook</p>
                <p className="text-xs text-muted-foreground leading-snug">
                  Authentic video content performs best on Instagram Reels and Facebook. We recommend posting to both channels for maximum reach.
                </p>
                <div className="flex gap-2 pt-0.5">
                  {activeSocialPlatforms.find(c => c.platform === 'instagram') && !crossPostPlatforms.includes('instagram') && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs gap-1.5"
                      onClick={() => onTogglePlatform('instagram')}
                      data-testid="nudge-add-instagram"
                    >
                      <SiInstagram className="h-3 w-3 text-[#E4405F]" />
                      Add Reels
                    </Button>
                  )}
                  {activeSocialPlatforms.find(c => c.platform === 'facebook') && !crossPostPlatforms.includes('facebook') && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs gap-1.5"
                      onClick={() => onTogglePlatform('facebook')}
                      data-testid="nudge-add-facebook"
                    >
                      <SiFacebook className="h-3 w-3 text-[#1877F2]" />
                      Add Facebook
                    </Button>
                  )}
                  {(!activeSocialPlatforms.find(c => c.platform === 'instagram') && !activeSocialPlatforms.find(c => c.platform === 'facebook')) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs gap-1.5"
                      onClick={() => window.location.href = '/partner/settings'}
                    >
                      <Link2 className="h-3 w-3" />
                      Connect accounts
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CircleDot className="h-4 w-4 text-primary" />
              <Label className="font-semibold">Eatzy Kudoz Feed</Label>
              <Badge className="text-xs bg-green-100 text-green-800 border-green-200">Always included</Badge>
            </div>
            <div className="pl-0">
              {isCarouselMode && carouselItems && carouselItems.length > 0 ? (
                <CarouselPreview items={carouselItems} caption={caption} restaurantName={restaurantName} />
              ) : (
                <EatzyPostMockup
                  caption={caption
                    .replace(/#\w+/g, '')
                    .replace(/https?:\/\/\S+/g, '')
                    .replace(/\n{3,}/g, '\n\n')
                    .trim()}
                  imageUrl={mediaImageUrl}
                  videoUrl={mediaVideoUrl}
                  restaurantName={restaurantName}
                  selectedItemName={selectedItem?.name}
                />
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <SiInstagram className="h-4 w-4 text-[#E4405F]" />
              <Label className="font-semibold">Instagram</Label>
              {igConnection ? (
                <Badge className="text-xs bg-green-100 text-green-800 border-green-200">Connected</Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">Not connected</Badge>
              )}
            </div>
            <div className="relative">
              {isCarouselMode && carouselItems && carouselItems.length > 0 ? (
                <CarouselPreview items={carouselItems} caption={caption} restaurantName={restaurantName} platform="instagram" />
              ) : (
                <InstagramMockup
                  displayText={caption}
                  imageUrl={mediaImageUrl}
                  videoUrl={mediaVideoUrl}
                  restaurantName={restaurantName}
                  hashtagsInFirstComment={crossPostPlatforms.includes('instagram') && igHashtagsInFirstComment}
                />
              )}
              {!igConnection && (
                <div className="absolute inset-0 rounded-lg bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2">
                  <SiInstagram className="h-6 w-6 text-[#E4405F]" />
                  <p className="text-sm font-medium text-foreground">Post to Instagram too</p>
                  <p className="text-xs text-muted-foreground text-center px-6">Connect your account to cross-post with one tap</p>
                  <Button size="sm" variant="outline" className="gap-1.5 mt-1" onClick={() => window.location.href = '/partner/settings'}>
                    <Link2 className="h-3.5 w-3.5" />
                    Connect Instagram
                  </Button>
                </div>
              )}
              {igConnection && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-end">
                    <button
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover-elevate',
                        crossPostPlatforms.includes('instagram') ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
                      )}
                      onClick={() => onTogglePlatform('instagram')}
                      data-testid="platform-instagram"
                    >
                      <div className={cn(
                        'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
                        crossPostPlatforms.includes('instagram') ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                      )}>
                        {crossPostPlatforms.includes('instagram') && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      {crossPostPlatforms.includes('instagram') ? 'Will post to Feed' : 'Include in post'}
                    </button>
                  </div>

                  {crossPostPlatforms.includes('instagram') && (
                    <div className="flex items-center justify-between py-1 px-0.5">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Post hashtags in first comment</span>
                      </div>
                      <Switch
                        checked={igHashtagsInFirstComment}
                        onCheckedChange={onIgHashtagsInFirstCommentChange}
                        data-testid="ig-hashtags-first-comment"
                        aria-label="Post hashtags in first comment"
                      />
                    </div>
                  )}

                  {crossPostPlatforms.includes('instagram') && orderingLink && !isCarouselMode && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-end">
                        <button
                          className={cn(
                            'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover-elevate',
                            crossPostPlatforms.includes('instagram_story') ? 'border-[#E4405F]/60 bg-[#E4405F]/5 text-[#E4405F]' : 'border-border text-muted-foreground'
                          )}
                          onClick={() => onTogglePlatform('instagram_story')}
                          data-testid="platform-instagram-story"
                        >
                          <div className={cn(
                            'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
                            crossPostPlatforms.includes('instagram_story') ? 'border-[#E4405F] bg-[#E4405F]' : 'border-muted-foreground/40'
                          )}>
                            {crossPostPlatforms.includes('instagram_story') && <Check className="h-2.5 w-2.5 text-white" />}
                          </div>
                          {crossPostPlatforms.includes('instagram_story') ? 'Story with tappable link' : 'Add Story with link'}
                        </button>
                      </div>

                      {crossPostPlatforms.includes('instagram_story') && (
                        <div className="space-y-1.5">
                          <p className="text-[11px] text-center text-muted-foreground">Story preview — tappable Link Sticker</p>
                          <InstagramStoryMockup
                            imageUrl={mediaImageUrl}
                            videoUrl={mediaVideoUrl}
                            restaurantName={restaurantName}
                            link={orderingLink}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <SiFacebook className="h-4 w-4 text-[#1877F2]" />
              <Label className="font-semibold">Facebook</Label>
              {fbConnection ? (
                <Badge className="text-xs bg-green-100 text-green-800 border-green-200">Connected</Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">Not connected</Badge>
              )}
            </div>
            <div className="relative">
              {isCarouselMode && carouselItems && carouselItems.length > 0 ? (
                <CarouselPreview items={carouselItems} caption={caption} restaurantName={restaurantName} platform="facebook" />
              ) : (
                <FacebookMockup
                  displayText={caption}
                  imageUrl={mediaImageUrl}
                  videoUrl={mediaVideoUrl}
                  restaurantName={restaurantName}
                  link={orderingLink}
                />
              )}
              {!fbConnection && (
                <div className="absolute inset-0 rounded-lg bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2">
                  <SiFacebook className="h-6 w-6 text-[#1877F2]" />
                  <p className="text-sm font-medium text-foreground">Share on Facebook too</p>
                  <p className="text-xs text-muted-foreground text-center px-6">Connect your page to cross-post automatically</p>
                  <Button size="sm" variant="outline" className="gap-1.5 mt-1" onClick={() => window.location.href = '/partner/settings'}>
                    <Link2 className="h-3.5 w-3.5" />
                    Connect Facebook
                  </Button>
                </div>
              )}
              {fbConnection && (
                <div className="mt-2 flex items-center justify-end">
                  <button
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover-elevate',
                      crossPostPlatforms.includes('facebook') ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
                    )}
                    onClick={() => onTogglePlatform('facebook')}
                    data-testid="platform-facebook"
                  >
                    <div className={cn(
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors',
                      crossPostPlatforms.includes('facebook') ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                    )}>
                      {crossPostPlatforms.includes('facebook') && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                    </div>
                    {crossPostPlatforms.includes('facebook') ? 'Will post' : 'Include in post'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {crossPostPlatforms.filter(p => p !== 'instagram_story').length > 0 && (
            <SocialMediaPreview
              content={{ text: caption, type: postType, link: orderingLink || undefined }}
              platforms={crossPostPlatforms.filter(p => p !== 'instagram_story')}
              restaurantName={restaurantName}
              imageUrl={mediaImageUrl}
              videoUrl={mediaVideoUrl}
              platformOverrides={platformOverrides}
              hashtagsInFirstComment={igHashtagsInFirstComment}
              onContentChange={onPlatformOverrideChange}
              onPreviewsLoaded={onPreviewsLoaded}
              onReset={onPlatformReset}
            />
          )}
        </div>
      </ScrollArea>

      <div className="p-4 border-t shrink-0">
        {!orderingLink && (
          <p className="text-xs text-center text-destructive mb-2">
            Restaurant ordering link unavailable. Please contact support to link your restaurant.
          </p>
        )}
        <Button
          className="w-full"
          size="lg"
          onClick={onPost}
          disabled={isPosting || !orderingLink}
          data-testid="button-post"
        >
          {isPosting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Posting…</>
          ) : (
            <><Check className="h-4 w-4 mr-2" />Post Now</>
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-2">
          {crossPostPlatforms.length > 0
            ? `Goes live on Eatzy + ${crossPostPlatforms.map(p => PLATFORM_META[p]?.label || p).join(', ')} with order link`
            : 'Posts to Eatzy Kudoz feed'}
        </p>
      </div>
    </div>
  );
}
